import React, { useState, useEffect, useMemo, useRef } from "react";
import { createRoot } from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme } from "../../../theme";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Card,
  CardContent,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import SubjectIcon from "@mui/icons-material/Subject";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EnhancedTable from "../../../components/common/EnhancedTable";
import MarkSheetFormModal, {
  MarksheetFormData,
} from "../../../components/Modals/MarkSheetFormModal";
import MarksEntryFormModal, {
  MarksEntryFormData,
} from "components/Modals/MarksEntryFormModal";
import {
  useMarkSheets,
  ExcelExportRequestDto,
  ExcelImportRequestDto,
  DeleteMarksheetRequestDto,
  DownloadMarksheetRequestDto,
} from "../../../hooks/useMarkSheets";
import { useAuth, AuthContext } from "../../../contexts/AuthContext";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useStudents } from "../../../hooks/useStudents";
import { useSchools } from "../../../hooks/useSchools";
import { useTeacher } from "../../../hooks/useTeacher";
import { useSubjects, useSubjectsByClass, useSubjectsByClassId } from "../../../hooks/useSubjects";
import { useMedium } from "../../../components/layout/Layout";
import { useTenantHeader } from "hooks/useTenantHeader";
import { useTranslation } from "react-i18next";
import { useGosvaraSheet } from "../../../hooks/useGosvaraSheet";
import { getStudentData } from "../../../utils/storage";
import { useError } from "../../../contexts/ErrorContext";
import {
  useMarksheetTypeOptions,
  useMonthTypeOptions,
  shouldUseNAForMonthType,
} from "../../../components/enum/MarksheetTypeOptions";
import MarksheetTemplate from "../../../components/templates/MarksheetTemplate";
import ClassFourthMarksheetTemplate from "../../../components/templates/ClassFourthMarksheetTemplate";
import ClassSixthMarksheetTemplate from "../../../components/templates/ClassSixthMarksheetTemplate";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublishIcon from "@mui/icons-material/Publish";
import { renderMarksheetTemplate, type MarksheetRenderData } from "components/marksheet-template-builder/renderTemplate";
import {
  listMarksheetTemplatesFromApi,
  getMarksheetTemplateFiles,
} from "services/marksheetTemplateApiService";

// Constants
const DEBOUNCE_DELAY = 300;
const SUCCESS_MESSAGE_DURATION = 5000;
const INFO_MESSAGE_DURATION = 3000;
const MAX_RETRY_ATTEMPTS = 4;
const A4_PAGE_WIDTH_MM = 210;
const A4_PAGE_HEIGHT_MM = 297;
const PDF_MARGIN_MM = 5;
const DEFAULT_MONTH_TYPE = "JANUARY";
const DEFAULT_MARKSHEET_TYPE = "QUARTERLY";
const DEFAULT_MEDIUM = "HINDI";
const MARKSHEET_FILTER_STORAGE_KEY = "marksheetTypeFilter";
/** This school always uses the main MarksheetTemplate (all classes, all marksheet types). */
const MARKSHEET_MAIN_TEMPLATE_SCHOOL_CODE = "462182";

// During bulk PDF generation, wait until student photo is no longer in "Loading..." state
// so html2canvas doesn't capture the transient placeholder on slow connections.
const waitForMarksheetPhotoToSettle = async (
  host: HTMLElement,
  opts?: { timeoutMs?: number; pollMs?: number }
) => {
  const timeoutMs = opts?.timeoutMs ?? 25000;
  const pollMs = opts?.pollMs ?? 120;
  const start = Date.now();

  // Wait until the loading marker disappears, and if an <img> exists, it's fully loaded.
  while (Date.now() - start < timeoutMs) {
    const loadingEl = host.querySelector('[data-ms-photo-state="loading"]');
    if (!loadingEl) {
      const img = host.querySelector(
        'img[data-ms-photo-state="ready"]'
      ) as HTMLImageElement | null;
      if (!img) return;
      if (img.complete && img.naturalWidth > 0) return;
    }
    await new Promise((r) => setTimeout(r, pollMs));
  }
};

// ------------------------------
// Debounce utility function
// ------------------------------
function useDebounce<T>(value: T, delay = DEBOUNCE_DELAY) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// ------------------------------
// QueryClient for bulk generation
// ------------------------------
const bulkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ------------------------------
// Get real user data from localStorage
// ------------------------------
const getRealAuthValue = () => {
  try {
    const token = localStorage.getItem("token");
    const userString = localStorage.getItem("user");
    const tenantSchema = localStorage.getItem("tenantSchema");

    if (token && userString) {
      const userData = JSON.parse(userString);

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      if (tenantSchema) {
        axios.defaults.headers.common["X-Tenant-ID"] = tenantSchema;
      }

      return {
        user: userData,
        isAuthenticated: true,
        isLoading: false,
        wasAutoLoggedOut: false,
        login: async () => userData,
        register: async () => userData,
        forgotPassword: async () => "",
        verifyOtp: async () => "",
        resetPassword: async () => "",
        logout: () => { },
        clearAutoLogoutFlag: () => { },
        isAdmin: () => userData.roles?.includes("ADMIN") || false,
        isSchoolAdmin: () => userData.roles?.includes("SCHOOL_ADMIN") || false,
        isTeacher: () => userData.roles?.includes("TEACHER") || false,
        isStudent: () => userData.roles?.includes("STUDENT") || false,
      };
    }
  } catch (err) {
  }

  const fallbackUser = {
    id: 1,
    username: "admin",
    email: "admin@example.com",
    fullName: "Administrator",
    schoolId: 1,
    roles: ["ADMIN"],
  };

  return {
    user: fallbackUser,
    isAuthenticated: true,
    isLoading: false,
    wasAutoLoggedOut: false,
    login: async () => fallbackUser,
    register: async () => fallbackUser,
    forgotPassword: async () => "",
    verifyOtp: async () => "",
    resetPassword: async () => "",
    logout: () => { },
    clearAutoLogoutFlag: () => { },
    isAdmin: () => true,
    isSchoolAdmin: () => false,
    isTeacher: () => false,
    isStudent: () => false,
  };
};

// ------------------------------
// Template wrapper with providers
// ------------------------------
const getBaseClassName = (cls: string): string => {
  const v = String(cls || "").trim();
  if (!v) return "";
  const match = v.match(/^(\d+(?:th|st|nd|rd)?)/i);
  return match ? match[1] : v.split(/\s+/)[0];
};

// Fetches student photo as base64 data URL so it can be embedded in a new browser tab.
// Blob URLs are scoped to the creating window and won't work across windows.
async function fetchStudentPhotoDataUrl(studentId: number): Promise<string | null> {
  try {
    const infoRes = await axios.get<{ fileName: string; filePath: string }>(
      `${process.env.REACT_APP_API_URL}/students/${studentId}/studentPhoto`
    );
    const fileName = infoRes.data?.fileName;
    if (!fileName) return null;

    const photoRes = await axios.get<Blob>(
      `${process.env.REACT_APP_API_URL}/students/photo/${encodeURIComponent(fileName)}`,
      { responseType: "blob" }
    );
    const blob = photoRes.data;
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const isClass3Or4ForMarksheet = (className: string) => {
  const base = getBaseClassName(className).toLowerCase();
  const baseNum = Number.parseInt(base, 10);
  return (
    baseNum === 3 ||
    baseNum === 4 ||
    base.includes("3rd") ||
    base.includes("4th")
  );
};

const isClass6Or7ForMarksheet = (className: string) => {
  const base = getBaseClassName(className).toLowerCase();
  const baseNum = Number.parseInt(base, 10);
  return (
    baseNum === 6 ||
    baseNum === 7 ||
    base.includes("6th") ||
    base.includes("7th")
  );
};

const MarksheetTemplateWithContext: React.FC<{
  student: any;
  schoolName: string;
  schoolCode: string;
  diseCode: string;
  block?: string;
  district?: string;
  marksheetType: string;
  sessionYear: string;
  className: string;
  subjects: any[];
  percentage: number;
  totalGrade: string;
  result: string;
  rank: number;
  failSubjects: string;
  teacherId?: number;
  onPrint?: () => void;
  isPdf?: boolean;
  hidePrintButton?: boolean;
  hideDownloadButton?: boolean;
  queryClient?: QueryClient;
  school?: any;
  totalTheoryObtained?: number;
  totalPracticalObtained?: number;
  totalObtained?: number;
  classId?: number;
  sessionId?: number;
  marksheet_assessment?: Record<string, any>;
}> = ({
  student,
  schoolName,
  schoolCode,
  diseCode,
  block,
  district,
  marksheetType,
  sessionYear,
  className,
  subjects,
  percentage,
  totalGrade,
  result,
  rank,
  failSubjects,
  teacherId,
  onPrint,
  isPdf,
  hidePrintButton,
  hideDownloadButton,
  queryClient,
  school,
  totalTheoryObtained,
  totalPracticalObtained,
  totalObtained,
  classId,
  sessionId,
  marksheet_assessment,
}) => {
    const authValue = getRealAuthValue();

    const clientToUse = queryClient || bulkQueryClient;

    const schoolCodeNorm = String(schoolCode || "").trim();
    const useMainTemplateOnly =
      schoolCodeNorm === MARKSHEET_MAIN_TEMPLATE_SCHOOL_CODE;
    const isAnnualMarksheet = marksheetType === "ANNUAL";

    let TemplateComponent: React.FC<any> = MarksheetTemplate;
    if (!useMainTemplateOnly) {
      if (isAnnualMarksheet && isClass3Or4ForMarksheet(className)) {
        TemplateComponent = ClassFourthMarksheetTemplate;
      } else if (isAnnualMarksheet && isClass6Or7ForMarksheet(className)) {
        TemplateComponent = ClassSixthMarksheetTemplate;
      }
    }

    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={clientToUse}>
          <AuthContext.Provider value={authValue}>
            <TemplateComponent
              student={student}
              schoolName={schoolName}
              schoolCode={schoolCode}
              diseCode={diseCode}
              block={block}
              district={district}
              marksheetType={marksheetType}
              sessionYear={sessionYear}
              className={className}
              subjects={subjects}
              percentage={percentage}
              totalGrade={totalGrade}
              result={result}
              rank={rank}
              failSubjects={failSubjects}
              teacherId={teacherId}
              onPrint={onPrint}
              isPdf={isPdf}
              hideDownloadButton={hideDownloadButton}
              school={school}
              totalTheoryObtained={totalTheoryObtained}
              totalPracticalObtained={totalPracticalObtained}
              totalObtained={totalObtained}
              classId={classId}
              sessionId={sessionId}
              marksheet_assessment={marksheet_assessment}
            />
          </AuthContext.Provider>
        </QueryClientProvider>
      </ThemeProvider>
    );
  };

// ------------------------------
// Utilities for reliable rendering
// ------------------------------
const waitNextFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

const waitForImages = async (el: HTMLElement) => {
  const imgs = Array.from(el.querySelectorAll("img"));
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise<void>((res) => {
          if (img.complete && img.naturalWidth) return res();
          img.addEventListener("load", () => res(), { once: true });
          img.addEventListener("error", () => res(), { once: true });
        })
    )
  );
};

const waitForFonts = async () => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const hasFonts = Boolean((document as any).fonts);
    const readyUnknown: unknown = (document as any).fonts?.ready;
    if (hasFonts && readyUnknown) {
      await (readyUnknown as Promise<void>);
    }
  } catch (err) {
  }
};

const hasRenderableContent = (node: HTMLElement) => {
  const txt = node.textContent?.trim() ?? "";
  const MIN_CONTENT_LENGTH = 20;
  return txt.length > MIN_CONTENT_LENGTH || node.children.length > 0;
};

const renderMarksheetOnce = async (
  data: any,
  TemplateWithProviders: React.FC<any>
): Promise<HTMLDivElement> => {
  const host = document.createElement("div");
  Object.assign(host.style, {
    position: "absolute",
    left: "-10000px",
    top: "0",
    width: "210mm",
    background: "#fff",
    margin: "0",
    padding: "0",
    boxSizing: "border-box",
    // Don't clip content; we scale-to-fit when adding to A4 PDF.
    overflow: "visible",
  } as CSSStyleDeclaration);
  document.body.appendChild(host);

  const root = createRoot(host);
  root.render(
    React.createElement(TemplateWithProviders, {
      ...data,
      hidePrintButton: true,
    })
  );
  (host as unknown as { __root?: unknown }).__root = root;

  await waitNextFrame();
  await waitNextFrame();
  await waitForFonts();
  await waitForImages(host);
  await waitNextFrame();

  if (!hasRenderableContent(host)) {
    for (let i = 0; i < MAX_RETRY_ATTEMPTS; i++) {
      await waitNextFrame();
      await waitForImages(host);
      if (hasRenderableContent(host)) break;
    }
  }

  return host;
};

const removeHost = (host: HTMLDivElement) => {
  try {
    const maybeRoot = (host as unknown as { __root?: { unmount?: () => void } }).__root;
    if (maybeRoot && typeof maybeRoot.unmount === "function") {
      maybeRoot.unmount();
    }
  } catch (err) {
  } finally {
    host.remove();
  }
};

const MarkSheetMarksManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showError, showSuccess } = useError();
  const queryClient = useQueryClient();

  // Get user from session storage to check role
  const getUserFromStorage = () => {
    try {
      const userString = sessionStorage.getItem('user') || localStorage.getItem('user');
      if (userString) {
        return JSON.parse(userString);
      }
    } catch (error) {
      console.warn('Failed to parse user from storage:', error);
    }
    return null;
  };

  const storedUser = getUserFromStorage();
  const isStudent = storedUser?.roles?.includes('STUDENT') || user?.roles?.includes('STUDENT');

  // Action Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handlePublishMarksheet = (row: any) => {
    handleMenuClose();
    if (!row.id || !row.student?.studentCode) {
      showError("Invalid marksheet data");
      return;
    }

    publishMarksheetMutation.mutate({
      marksheetId: row.id,
      studentCode: row.student.studentCode,
      published: !row.published // Toggle status
    }, {
      onSuccess: () => {
        showSuccess(row.published ? "Marksheet unpublished successfully" : "Marksheet published successfully");
      },
      onError: (error: any) => {
        showError(error.response?.data?.message || "Failed to update publish status");
      }
    });
  };

  const { selectedClass, selectedSession } = useSessionClass();
  const marksheetTypeOptions = useMarksheetTypeOptions(selectedClass?.name);
  const monthTypeOptions = useMonthTypeOptions();
  const { selectedMedium } = useMedium();
  const schoolId = user?.schoolId ?? 0;
  const { tenantLoading } = useTenantHeader();

  // Ensure promotion status / delete availability is always fresh when landing here
  // (e.g., after demote/promote actions from other screens).
  useEffect(() => {
    if (tenantLoading) return;

    queryClient.invalidateQueries({ queryKey: ["marksheets"] });
    queryClient.invalidateQueries({
      queryKey: [
        "marksheets",
        selectedClass?.id ?? null,
        selectedSession?.id ?? null,
      ],
    });
    // Invalidate all server-paged marksheet queries (full key includes className, page index, size).
    queryClient.invalidateQueries({ queryKey: ["marksheetsPage"] });
    queryClient.invalidateQueries({ queryKey: ["marksheetsSearchByName"] });
  }, [queryClient, tenantLoading, selectedClass?.id, selectedSession?.id]);

  // Server-side pagination: table page/size are sent as API page/size.
  const [tablePage, setTablePage] = useState(0);
  const [tableRowsPerPage, setTableRowsPerPage] = useState(10);

  const [tableFilterMarksheetType, setTableFilterMarksheetType] = useState<string>(() => {
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isPageRefresh = navEntries.length > 0 && navEntries[0].type === 'reload';

    const persistedValue = sessionStorage.getItem(MARKSHEET_FILTER_STORAGE_KEY);
    const sessionActiveFlag = sessionStorage.getItem(`${MARKSHEET_FILTER_STORAGE_KEY}_active`);

    if (isPageRefresh) {
      sessionStorage.removeItem(MARKSHEET_FILTER_STORAGE_KEY);
      sessionStorage.removeItem(`${MARKSHEET_FILTER_STORAGE_KEY}_active`);
      return "";
    }

    if (persistedValue && sessionActiveFlag === "true") {
      return persistedValue;
    }

    if (persistedValue) {
      sessionStorage.removeItem(MARKSHEET_FILTER_STORAGE_KEY);
      sessionStorage.removeItem(`${MARKSHEET_FILTER_STORAGE_KEY}_active`);
    }
    return "";
  });

  useEffect(() => {
    sessionStorage.setItem(`${MARKSHEET_FILTER_STORAGE_KEY}_active`, "true");

    if (tableFilterMarksheetType) {
      sessionStorage.setItem(MARKSHEET_FILTER_STORAGE_KEY, tableFilterMarksheetType);
    } else {
      sessionStorage.removeItem(MARKSHEET_FILTER_STORAGE_KEY);
    }
  }, [tableFilterMarksheetType]);

  const selectedClassBaseForTableFilter = getBaseClassName(String(selectedClass?.name || "")).toLowerCase();
  const isSelectedClass11thForTableFilter =
    Number.parseInt(selectedClassBaseForTableFilter, 10) === 11 ||
    selectedClassBaseForTableFilter.includes("11th");

  const effectiveTableFilterMarksheetTypeForApi =
    tableFilterMarksheetType === "FINAL" && !isSelectedClass11thForTableFilter
      ? "ANNUAL"
      : tableFilterMarksheetType;

  const isAllClassForApi =
    selectedClass?.section === "__GRADE_ALL__" ||
    selectedClass?.name === "ALL" ||
    String(selectedClass?.name || "").includes("-ALL");

  const allClassNameForApi =
    !isAllClassForApi
      ? ""
      : String(selectedClass?.name || "").includes("-ALL")
        ? String(selectedClass?.name || "")
        : selectedClass?.name
          ? `${selectedClass.name}-ALL`
          : "";

  const isAllClassRowMatch = (rowClassName: unknown): boolean => {
    if (!isAllClassForApi) return false;
    const baseSelected = getBaseClassName(String(allClassNameForApi || "")).toLowerCase();
    const baseRow = getBaseClassName(String(rowClassName || "")).toLowerCase();
    if (!baseSelected || !baseRow) return false;
    return baseSelected === baseRow;
  };

  const {
    marksheetsQuery,
    marksheetsPageQuery,
    marksheetsByTypePageQuery,
    marksheetsByClassIdAndSessionIdQuery,
    createMarksheetMutation,
    updateMarksheetMutation,
    deleteMarksheetMutation,
    exportExcelMutation,
    importExcelMutation,
    downloadMarksheetMutation,
    addMarksEntryMutation,
    useMarksheetByStudentId,
    useMarksheetByStudentCode,
    useAllMarksEntries,
    useSearchMarksheetsByName,
    publishMarksheetMutation,
    publishBulkMarksheetsMutation,
  } = useMarkSheets(
    tenantLoading,
    isStudent,
    isStudent
      ? undefined
      : isAllClassForApi
        ? undefined
        : selectedClass?.id,
    isStudent ? undefined : selectedSession?.id,
    isStudent ? undefined : tablePage,
    isStudent ? undefined : tableRowsPerPage,
    true,
    isStudent
      ? undefined
      : isAllClassForApi
        ? allClassNameForApi
        : undefined
    ,
    isStudent ? undefined : (effectiveTableFilterMarksheetTypeForApi || null)
  ); // Disable marksheetsQuery for students; list by class + session
  const {
    studentsQuery,
    useStudentsByClassId,
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    useStudentByMobileNo,
  } = useStudents();

  const subjectsByClassIdQuery = useSubjectsByClassId(
    isAllClassForApi ? 0 : (selectedClass?.id ?? 0)
  );
  const subjectsByClassNameQuery = useSubjectsByClass(allClassNameForApi);
  const classSubjects = (isAllClassForApi
    ? subjectsByClassNameQuery.data
    : subjectsByClassIdQuery.data) || [];
  // Disable studentsByClassQuery for students
  const studentsByClassQuery = useStudentsByClassId(
    isStudent ? 0 : (isAllClassForApi ? 0 : (selectedClass?.id ?? 0))
  );

  // Use filtered API query when both session and class are selected, but disable for students
  const studentsBySessionIdAndClassIdQuery = useStudentsBySessionIdAndClassId(
    isStudent ? undefined : selectedSession?.id,
    isStudent ? undefined : selectedClass?.id
  );

  const studentsBySessionIdAndClassNameQuery = useStudentsBySessionIdAndClassName(
    isStudent ? undefined : selectedSession?.id,
    isStudent ? undefined : allClassNameForApi
  );

  const filteredStudentsFromAPI = (isAllClassForApi
    ? studentsBySessionIdAndClassNameQuery.data
    : studentsBySessionIdAndClassIdQuery.data) || [];

  const studentData = isStudent ? getStudentData() : null;
  const studentId = studentData?.id ? Number(studentData.id) : null;

  // For students: Fetch student data using mobile number API to get studentCode
  const studentMobile = isStudent ? (user?.email || "") : "";
  const isValidMobile = studentMobile && !studentMobile.includes('@') && /^\d{10}$/.test(studentMobile.replace(/\D/g, ''));

  const {
    data: studentDataByMobile,
    isLoading: loadingStudentByMobile
  } = useStudentByMobileNo(
    isStudent && isValidMobile ? studentMobile : undefined,
    isStudent && isValidMobile ? selectedSession?.id : undefined,
    isStudent && isValidMobile ? selectedClass?.id : undefined
  );

  // Extract studentCode from the fetched student data
  const studentCode = studentDataByMobile?.studentCode;

  // For students: Use marksheetByStudentCode API when studentCode is available
  const studentMarksheetByCodeQuery = useMarksheetByStudentCode(
    isStudent ? studentCode : undefined,
    isStudent && !!studentCode
  );

  // Keep the old query for backward compatibility (non-student users)
  const studentMarksheetQuery = useMarksheetByStudentId(
    studentId,
    isStudent && !!studentId && !studentCode // Only use if studentCode is not available
  );

  const marksEntriesQuery = useAllMarksEntries(
    selectedClass?.id ?? null,
    selectedSession?.id ?? null,
    !!(selectedClass?.id && selectedSession?.id && !isStudent)
  );

  const { useAllGosvaraData } = useGosvaraSheet();
  // Disable gosvaraDataQuery for students
  const gosvaraDataQuery = useAllGosvaraData(
    schoolId,
    selectedSession?.id ?? 0,
    selectedMedium || DEFAULT_MEDIUM,
    selectedClass?.id ?? undefined,
    undefined,
    !!(schoolId && selectedClass?.id && selectedSession?.id && selectedMedium && !isStudent)
  );

  const { useSchoolById } = useSchools();
  const { data: schoolData } = useSchoolById(schoolId);
  const schoolAny: any = schoolData;
  const schoolBlock: string = schoolAny?.blockName || schoolAny?.block || "";
  const schoolDistrict: string = schoolAny?.district || "";

  const { useTeachersBySchool } = useTeacher();
  const { data: teachersData } = useTeachersBySchool(isStudent ? 0 : schoolId);

  const getDefaultTeacherId = (className?: string): number | undefined => {
    if (teachersData && teachersData.length > 0) {
      const activeTeachers = teachersData.filter((teacher) => {
        const isActive = teacher.isActive !== false;
        const hasStatus = teacher.status !== false;
        return isActive && hasStatus;
      });

      if (className && className.trim()) {
        const normalizedClassName = className.trim().toLowerCase();
        const classTeachers = activeTeachers.filter((teacher) => {
          if (teacher.teacherOfClass && Array.isArray(teacher.teacherOfClass)) {
            return teacher.teacherOfClass.some((assignedClass: string) => {
              const normalizedAssignedClass = assignedClass.trim().toLowerCase();
              return (
                normalizedAssignedClass === normalizedClassName ||
                normalizedClassName.includes(normalizedAssignedClass) ||
                normalizedAssignedClass.includes(normalizedClassName)
              );
            });
          }
          return false;
        });

        if (classTeachers.length > 0) {
          return classTeachers[0].id;
        }
      }

      if (activeTeachers.length > 0) {
        return activeTeachers[0].id;
      }

      return teachersData[0].id;
    }
    return undefined;
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentMarksheet, setCurrentMarksheet] = useState<
    MarksheetFormData | undefined
  >(undefined);
  const [marksEntryModalOpen, setMarksEntryModalOpen] = useState(false);
  const [viewMarksEntriesModalOpen, setViewMarksEntriesModalOpen] = useState(false);
  const [viewMarksEntriesMarksheetType, setViewMarksEntriesMarksheetType] =
    useState<string>(DEFAULT_MARKSHEET_TYPE);
  const [viewMarksEntriesMonthType, setViewMarksEntriesMonthType] =
    useState<string>("NA");

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [selectedMarksheetType, setSelectedMarksheetType] =
    useState<string>("");
  const selectedClassBaseForBulk = getBaseClassName(String(selectedClass?.name || "")).toLowerCase();
  const isSelectedClass11thForBulk =
    Number.parseInt(selectedClassBaseForBulk, 10) === 11 ||
    selectedClassBaseForBulk.includes("11th");
  const effectiveSelectedMarksheetType =
    selectedMarksheetType === "FINAL" && !isSelectedClass11thForBulk
      ? "ANNUAL"
      : selectedMarksheetType;
  const [bulkMonthType, setBulkMonthType] = useState<string>(DEFAULT_MONTH_TYPE);
  const [bulkDownloadSelectedMarksheetIds, setBulkDownloadSelectedMarksheetIds] = useState<
    Set<number>
  >(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const isBulkDownloadCancelledRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [currentStudent, setCurrentStudent] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [submittedSearchName, setSubmittedSearchName] = useState("");
  const debouncedSearch = useDebounce(searchTerm, DEBOUNCE_DELAY);
  const isNameSearchActive =
    !isStudent && String(submittedSearchName || "").trim().length > 0;
  const isServerNameSearchActive = isNameSearchActive;

  const marksheetsSearchByNameQuery = useSearchMarksheetsByName(
    isServerNameSearchActive
      ? {
        ...(isAllClassForApi ? { className: allClassNameForApi } : { classId: selectedClass?.id }),
        sessionId: selectedSession?.id ?? 0,
        name: String(submittedSearchName || "").trim(),
        page: tablePage,
        size: tableRowsPerPage,
        // matches backend @PageableDefault(sort="student.studentName", direction=ASC)
        sort: "student.studentName,asc",
      }
      : null,
    isServerNameSearchActive
  );

  // When server-paginated list filters change, reset to first page.
  useEffect(() => {
    setTablePage(0);
  }, [
    tableFilterMarksheetType,
    submittedSearchName,
    selectedClass?.id,
    selectedSession?.id,
  ]);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importMarksheetType, setImportMarksheetType] =
    useState<string>(DEFAULT_MARKSHEET_TYPE);
  const [importMonthType, setImportMonthType] = useState<string>(
    shouldUseNAForMonthType(DEFAULT_MARKSHEET_TYPE) ? "NA" : DEFAULT_MONTH_TYPE
  );
  const [importErrorFile, setImportErrorFile] = useState<Blob | null>(null);

  const [downloadMonthTypeModalOpen, setDownloadMonthTypeModalOpen] =
    useState(false);
  const [downloadMonthType, setDownloadMonthType] = useState<string>(DEFAULT_MONTH_TYPE);

  const [marksheetViewDialogOpen, setMarksheetViewDialogOpen] = useState(false);
  const [selectedMarksheetForView, setSelectedMarksheetForView] =
    useState<any>(null);
  const [customMarksheetDialogOpen, setCustomMarksheetDialogOpen] = useState(false);
  const [customMarksheetPreviewData, setCustomMarksheetPreviewData] = useState<{
    html: string;
    css: string;
    studentName: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [marksheetToDelete, setMarksheetToDelete] = useState<any>(null);

  // Bulk publish/unpublish state
  const [bulkPublishDialogOpen, setBulkPublishDialogOpen] = useState(false);
  const [bulkPublishAction, setBulkPublishAction] = useState<"PUBLISH" | "UNPUBLISH">("PUBLISH");
  const [bulkPublishMarksheetType, setBulkPublishMarksheetType] = useState<string>("");
  const [bulkPublishMonthType, setBulkPublishMonthType] = useState<string>("");
  const [bulkSelectedMarksheetIds, setBulkSelectedMarksheetIds] = useState<Set<number>>(new Set());

  const bulkModalFilteredMarksheets = useMemo(() => {
    const sourceMarksheets: any[] = (marksheetsByClassIdAndSessionIdQuery.data || []) as any[];
    return sourceMarksheets
      .filter((marksheet) => {
        const basicFilter =
          marksheet.schoolId === schoolId &&
          marksheet.marksheetType === effectiveSelectedMarksheetType;

        if (effectiveSelectedMarksheetType === "MONTHLY") {
          return basicFilter && marksheet.monthType === bulkMonthType;
        }

        return basicFilter;
      })
      .slice()
      .sort((a: any, b: any) => {
        const getName = (ms: any) => {
          const s = ms.student || {};
          const direct =
            s.studentName ||
            (s.firstName && s.lastName
              ? `${s.firstName} ${s.lastName}`
              : s.firstName || s.lastName || "");
          return String(direct || "").toLowerCase();
        };
        return getName(a).localeCompare(getName(b));
      });
  }, [
    marksheetsByClassIdAndSessionIdQuery.data,
    schoolId,
    effectiveSelectedMarksheetType,
    bulkMonthType,
  ]);

  useEffect(() => {
    const ids = bulkModalFilteredMarksheets
      .map((m: any) => m.id)
      .filter((id: unknown): id is number => typeof id === "number");
    setBulkDownloadSelectedMarksheetIds(new Set(ids));
  }, [selectedMarksheetType, bulkMonthType, bulkModalFilteredMarksheets]);

  const bulkMarksheetsSelectAllChecked =
    bulkModalFilteredMarksheets.length > 0 &&
    bulkModalFilteredMarksheets.every(
      (m: any) => typeof m.id === "number" && bulkDownloadSelectedMarksheetIds.has(m.id)
    );
  const bulkMarksheetsSelectAllIndeterminate =
    bulkDownloadSelectedMarksheetIds.size > 0 && !bulkMarksheetsSelectAllChecked;

  const openBulkPublishDialog = () => {
    setBulkPublishDialogOpen(true);
    setBulkSelectedMarksheetIds(new Set());
    setBulkPublishAction("PUBLISH");
    setBulkPublishMarksheetType("");
    setBulkPublishMonthType("");
  };

  const closeBulkPublishDialog = () => {
    if (publishBulkMarksheetsMutation.isLoading) return;
    setBulkPublishDialogOpen(false);
    setBulkSelectedMarksheetIds(new Set());
    setBulkPublishAction("PUBLISH");
    setBulkPublishMarksheetType("");
    setBulkPublishMonthType("");
  };

  const toggleBulkSelected = (marksheetId: number) => {
    setBulkSelectedMarksheetIds((prev) => {
      const next = new Set(prev);
      if (next.has(marksheetId)) next.delete(marksheetId);
      else next.add(marksheetId);
      return next;
    });
  };

  const getStudentName = (studentId: number) => {
    const student = (studentsQuery.data || []).find((s) => s.id === studentId);
    return student ? `${student.studentName}` : studentId;
  };

  const toText = (value: unknown): string => {
    if (typeof value === "string" || typeof value === "number") return String(value);
    return "";
  };

  const handleViewMarksheet = async (row: any) => {
    const rowClassName: string = row.className || row.student?.className || "";
    // Templates are stored by base class name (e.g. "9th"), not section (e.g. "9th B")
    const classNameForTemplate: string = getBaseClassName(rowClassName) || rowClassName;
    const sid = selectedSession?.id;

    // Check if there's a primary custom template for this class+session
    try {
      if (rowClassName && sid) {
        const apiTemplates = await listMarksheetTemplatesFromApi(classNameForTemplate, sid);
        const primaryTpl = apiTemplates.find((t) => t.primary);
        if (primaryTpl) {
          const [{ htmlContent, cssContent }, photoDataUrl] = await Promise.all([
            getMarksheetTemplateFiles(primaryTpl.htmlFile, primaryTpl.cssFile),
            row.student?.id ? fetchStudentPhotoDataUrl(row.student.id) : Promise.resolve(null),
          ]);
          if (htmlContent) {
            const renderData: MarksheetRenderData = {
              school: {
                name: schoolData?.nameofSchool || row.schoolName || "",
                code: schoolData?.schoolCode || "",
                diseCode: schoolData?.diseCode || "",
                block: schoolBlock,
                district: schoolDistrict,
              },
              exam: {
                type: row.marksheetType || "",
                sessionYear: row.sessionYear || "",
              },
              student: {
                name: row.studentName || row.student?.studentName || "",
                rollNo: row.rollNumber || row.student?.rollNumber || "",
                enrollmentNo: row.student?.enrolmentNo || "",
                fatherName: row.student?.fatherName || "",
                motherName: row.student?.motherName || "",
                dob: row.student?.dateOfBirth,
                className: rowClassName,
                photo: photoDataUrl ?? undefined,
              },
              subjects: (row.subjects || []).map((s: any) => ({
                subjectCode: s.subjectCode || "",
                subjectName: s.subjectName || "",
                theoryMarks: s.theoryMarks ?? null,
                practicalMarks: s.practicalMarks ?? null,
                grade: s.grade,
                remark: s.remark,
              })),
              summary: {
                percentage: row.percentage ?? 0,
                totalGrade: row.totalGrade || "",
                result: row.result || "",
                rank: row.rank,
                failSubjects: row.failSubjects || "",
              },
            };
            const { html: rendered, css: renderedCss } = renderMarksheetTemplate(
              htmlContent,
              cssContent,
              renderData
            );
            setCustomMarksheetPreviewData({
              html: rendered,
              css: renderedCss,
              studentName: renderData.student.name,
            });
            setCustomMarksheetDialogOpen(true);
            return;
          }
        }
      }
    } catch {
      // If anything fails, fall through to the default dialog
    }

    // Fallback: existing React-based marksheet dialog
    setSelectedMarksheetForView(row);
    setMarksheetViewDialogOpen(true);
  };

  const handleEditMarksheet = (row: any) => {
    handleMenuClose();
    setIsEdit(true);

    const convertedSubjects =
      row.subjects?.map((sub: Record<string, unknown>) => {
        return {
          subjectCode: toText(sub.subjectCode || sub.subject || (sub as any).code || ""),
          subjectName: toText(sub.subjectName || (sub as any).name || sub.subject || ""),
          theoryMarks:
            sub.theoryMarks === -1
              ? null
              : (sub.theoryMarks ?? sub.obtainedTheoryMarks ?? null) as number | null,
          practicalMarks:
            sub.practicalMarks === -1
              ? null
              : (sub.practicalMarks ?? sub.practicalMarksObtained ?? sub.obtainedPracticalMarks ?? null) as number | null,
          quarterlyWeightage: (sub.quarterlyWeightage as number) ?? undefined,
          halfYearlyWeightage: (sub.halfYearlyWeightage as number) ?? undefined,
          originalPracticalMarks: (sub.originalPracticalMarks as number) ?? undefined,
          remark: toText(sub.remark || sub.remarks || ""),
        };
      }) || [];

    const studentObj = row.student as Record<string, unknown> | undefined;
    const schoolClassObj = studentObj?.schoolClass as Record<string, unknown> | undefined;

    setCurrentMarksheet({
      id: row.id,
      studentId: (row.studentId ?? studentObj?.id) as number,
      schoolId: row.schoolId,
      classId: (row.classId ?? schoolClassObj?.id) as number,
      marksheetType: row.marksheetType,
      sessionYear: row.sessionYear,
      className: row.className,
      monthType: row.monthType || t("january"),
      subjects: convertedSubjects,
      marksheet_assessment: row.marksheet_assessment || {},
    });
    setModalOpen(true);
  };

  const handleCloseMarksheetView = () => {
    setMarksheetViewDialogOpen(false);
    setSelectedMarksheetForView(null);
  };

  const handleDeleteMarksheet = (row: any) => {
    setMarksheetToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteMarksheet = () => {
    if (!selectedClass || !selectedSession || !marksheetToDelete) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    const promotionStatus =
      marksheetToDelete?.promote ??
      marksheetToDelete?.promotionStatus ??
      marksheetToDelete?.promotion ??
      marksheetToDelete?.student?.promote ??
      marksheetToDelete?.student?.promotionStatus ??
      marksheetToDelete?.student?.promotion;

    if (String(promotionStatus).toUpperCase() === "PROMOTED") {
      showError(
        t("cannot_delete_promoted_student_marksheet") ||
          "Student is already promoted cannot delete its marksheet"
      );
      setDeleteDialogOpen(false);
      setMarksheetToDelete(null);
      return;
    }

    const deleteData: DeleteMarksheetRequestDto = {
      schoolId: schoolId,
      classId: marksheetToDelete.classId ?? marksheetToDelete.student?.schoolClass?.id,
      sessionId: selectedSession.id!,
      studentId: marksheetToDelete.studentId ?? marksheetToDelete.student?.id,
      marksheetType: marksheetToDelete.marksheetType,
      monthType: marksheetToDelete.monthType || DEFAULT_MONTH_TYPE,
    };

    deleteMarksheetMutation.mutate(deleteData, {
      onSuccess: () => {
        showSuccess(
          t("marksheet_deleted_successfully").replace(
            "{studentName}",
            marksheetToDelete.studentName || "student"
          )
        );
        setDeleteDialogOpen(false);
        setMarksheetToDelete(null);
      },
      onError: (error: any) => {
        showError(
          t("failed_to_delete_marksheet").replace(
            "{error}",
            error?.response?.data?.message || error?.message || "Unknown error"
          )
        );
        setDeleteDialogOpen(false);
        setMarksheetToDelete(null);
      },
    });
  };

  const handleExcelExport = () => {
    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    if (!importMarksheetType) {
      showError(t("please_select_marksheet_type"));
      return;
    }

    const effectiveImportMarksheetType =
      importMarksheetType === "FINAL" ? "ANNUAL" : importMarksheetType;

    const exportParams: ExcelExportRequestDto = {
      schoolId: schoolId,
      classId: selectedClass.id!,
      sessionId: selectedSession.id!,
      marksheetType: effectiveImportMarksheetType as any,
      monthType: shouldUseNAForMonthType(effectiveImportMarksheetType) ? "NA" : importMonthType,
    };

    exportExcelMutation.mutate(exportParams, {
      onSuccess: () => {
        showSuccess(t("excel_file_downloaded_successfully"));
      },
      onError: (error: any) => {
        showError(
          t("excel_export_failed").replace(
            "{error}",
            error?.response?.data?.message || error?.message || "Unknown error"
          )
        );
      },
    });
  };

  const handleOpenImportModal = () => {
    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }
    setImportModalOpen(true);
    setSelectedFile(null);
    setImportErrorFile(null);
  };

  const handleCloseImportModal = () => {
    setImportModalOpen(false);
    setSelectedFile(null);
    setImportMarksheetType(DEFAULT_MARKSHEET_TYPE);
    setImportMonthType(DEFAULT_MONTH_TYPE);
    setImportErrorFile(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportErrorFile(null);
    }
  };

  const handleExcelImport = async () => {
    if (!selectedFile) {
      showError(t("please_select_file"));
      return;
    }

    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    setImportErrorFile(null);

    const effectiveImportMarksheetType =
      importMarksheetType === "FINAL" ? "ANNUAL" : importMarksheetType;

    const importParams: ExcelImportRequestDto = {
      file: selectedFile,
      schoolId: schoolId,
      classId: selectedClass.id!,
      sessionId: selectedSession.id!,
      sessionYear: selectedSession.name || "",
      marksheetType: effectiveImportMarksheetType,
      monthType: shouldUseNAForMonthType(effectiveImportMarksheetType) ? "NA" : importMonthType,
    };

    try {
      const result = await importExcelMutation.mutateAsync(importParams);

      if (result.success) {
        setImportModalOpen(false);
        setSelectedFile(null);
        setImportMarksheetType(DEFAULT_MARKSHEET_TYPE);
        setImportMonthType(DEFAULT_MONTH_TYPE);
        showSuccess(t("excel_file_imported_successfully"));
      } else {
        if (!result.errorFile) {
          showError(result.message);
        }
        setImportErrorFile(result.errorFile || null);
      }
    } catch (error: any) {
      if (error?.response?.data?.errorFile) {
        setImportErrorFile(error.response.data.errorFile);
      } else {
        let errorMessage = error.message || t("excel_import_failed");

        if (
          errorMessage.includes("No subject columns found") ||
          errorMessage.includes("SUB1 CODE") ||
          errorMessage.includes("TH1") ||
          errorMessage.includes("Excel import failed")
        ) {
          errorMessage = "Please upload excel in proper format";
        }

        showError(errorMessage);
      }
    }
  };

  const handleDownloadErrorFile = () => {
    if (importErrorFile) {
      const url = window.URL.createObjectURL(importErrorFile);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'marksheet_import_errors.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      handleCloseImportModal();
    }
  };

  const handleDownloadMarksheet = () => {
    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    if (!importMarksheetType) {
      showError(t("please_select_marksheet_type"));
      return;
    }

    setDownloadMonthTypeModalOpen(true);
  };

  const handleDownloadMarksheetWithMonthType = () => {
    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    const selectedClassName = String(selectedClass?.name ?? "").toLowerCase();
    const isClass3rd4th6th7thForDownload =
      selectedClassName.includes("3rd") ||
      selectedClassName.includes("4th") ||
      selectedClassName.includes("6th") ||
      selectedClassName.includes("7th");

    const effectiveImportMarksheetType =
      importMarksheetType === "FINAL" ? "ANNUAL" : importMarksheetType;

    const isSahshekshikDownload =
      isClass3rd4th6th7thForDownload && importMarksheetType === "SAHSHEKSHIK";

    const marksheetTypeToSend = isSahshekshikDownload ? "ANNUAL" : effectiveImportMarksheetType;

    const downloadParams: DownloadMarksheetRequestDto = {
      classId: isAllClassForApi ? null : selectedClass.id!,
      className: isAllClassForApi ? allClassNameForApi : null,
      sessionId: selectedSession.id!,
      marksheetType: marksheetTypeToSend,
      monthType: shouldUseNAForMonthType(marksheetTypeToSend) ? "NA" : downloadMonthType,
      sahshekshik: isSahshekshikDownload,
    };

    downloadMarksheetMutation.mutate(downloadParams, {
      onSuccess: () => {
        showSuccess(t("marksheet_downloaded_successfully"));
        setDownloadMonthTypeModalOpen(false);
      },
      onError: (error: any) => {
        if (error?.response?.status === 404) {
          const marksheetTypeLabel = marksheetTypeOptions.find(
            (opt) => opt.value === importMarksheetType
          )?.label || importMarksheetType;
          const monthTypeLabel = monthTypeOptions.find(
            (opt) => opt.value === downloadMonthType
          )?.label || downloadMonthType;
          const className = selectedClass?.name || "Unknown Class";
          const sessionName = selectedSession?.name || "Unknown Session";

          showError(
            t("marksheet_type_not_found_for_download")
              .replace("{marksheetType}", marksheetTypeLabel)
              .replace("{monthType}", monthTypeLabel)
              .replace("{className}", className)
              .replace("{sessionName}", sessionName)
          );
        } else {
          showError(
            t("download_marksheet_failed").replace(
              "{error}",
              error?.response?.data?.message || error?.message || "Unknown error"
            )
          );
        }
      },
    });
  };

  const handleCloseDownloadMonthTypeModal = () => {
    setDownloadMonthTypeModalOpen(false);
    setDownloadMonthType(DEFAULT_MONTH_TYPE);
  };

  const handleOpenBulkModal = () => {
    setBulkModalOpen(true);
    // When bulk modal opens, fetch ALL marksheets for selected class + session
    // using GET /marksheets/classId/sessionId
    if (!isAllClassForApi && selectedClass?.id && selectedSession?.id) {
      void marksheetsByClassIdAndSessionIdQuery.refetch();
    }
  };

  const handleCloseBulkModal = () => {
    if (isGenerating) return;
    setBulkModalOpen(false);
    setSelectedMarksheetType("");
    setBulkMonthType(DEFAULT_MONTH_TYPE);
    setProgress(0);
    setCurrentStudent("");
    setBulkDownloadSelectedMarksheetIds(new Set());
    isBulkDownloadCancelledRef.current = false;
  };

  const handleCancelBulkDownload = () => {
    isBulkDownloadCancelledRef.current = true;
    setIsGenerating(false);
    setProgress(0);
    setCurrentStudent("");
    showError(t("bulk_download_cancelled") || "Bulk download was cancelled by user");
  };

  const handleBulkDownload = async () => {
    setProgress(0);
    setCurrentStudent("");

    if (!selectedMarksheetType) {
      showError(t("please_select_marksheet_type"));
      return;
    }

    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    // Bulk download must fetch ALL marksheets for selected class + session
    // using GET /marksheets/classId/sessionId.
    if (isAllClassForApi) {
      showError(t("please_select_class") || "Please select a specific class (not ALL).");
      return;
    }

    const refetchResult = await marksheetsByClassIdAndSessionIdQuery.refetch();
    const sourceMarksheets: any[] = (refetchResult.data || []) as any[];

    const filteredMarksheets = (sourceMarksheets || []).filter(
      (marksheet) => {
        // API already filtered by classId + sessionId; here we only filter by type (and monthType for MONTHLY)
        const basicFilter =
          marksheet.schoolId === schoolId &&
          marksheet.marksheetType === effectiveSelectedMarksheetType;

        if (effectiveSelectedMarksheetType === "MONTHLY") {
          return basicFilter && marksheet.monthType === bulkMonthType;
        }

        return basicFilter;
      }
    );

    if (filteredMarksheets.length === 0) {
      showError(
        t("no_marksheets_found_for_type")
          .replace("{type}", selectedMarksheetType)
          .replace("{className}", selectedClass.name)
          .replace("{sessionName}", selectedSession?.name || "")
      );
      return;
    }

    const marksheetsToProcess = filteredMarksheets.filter(
      (m: any) => typeof m.id === "number" && bulkDownloadSelectedMarksheetIds.has(m.id)
    );

    if (marksheetsToProcess.length === 0) {
      showError(
        (t("please_select_students_to_download") as string) ||
          "Please select at least one marksheet to download."
      );
      return;
    }

    // NEW: sort marksheets by student name (A-Z)
    marksheetsToProcess.sort((a: any, b: any) => {
      const getName = (ms: any) => {
        const s = ms.student || {};
        const direct =
          s.studentName ||
          (s.firstName && s.lastName
            ? `${s.firstName} ${s.lastName}`
            : s.firstName || s.lastName || "");
        return String(direct || "").toLowerCase();
      };
      return getName(a).localeCompare(getName(b));
    });

    setIsGenerating(true);
    isBulkDownloadCancelledRef.current = false;

    try {
      const className = selectedClass.name || "Unknown";
      const sessionName = selectedSession?.name || "Unknown";
      const filename = `bulk-marksheets-${selectedMarksheetType || effectiveSelectedMarksheetType}-${className}-${sessionName}-${marksheetsToProcess.length}-students.pdf`;

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: false,
      });

      let pageIndex = 0;

      for (let i = 0; i < marksheetsToProcess.length; i++) {
        // Check for cancellation
        if (isBulkDownloadCancelledRef.current) {
          break;
        }
        const marksheet = marksheetsToProcess[i];
        const studentData = marksheet.student as any;

        setCurrentStudent(
          studentData?.studentName || studentData?.name || "Unknown Student"
        );

        const marksheetData = {
          // Some backend fields (block/district) are not typed on this hook's SchoolData.
          // Use a safe escape hatch to avoid TS errors while still rendering the values.
          schoolMeta: schoolData as any,
          student: {
            // Needed by `MarksheetTemplate` -> `useStudentPhoto(studentId)` for photo rendering,
            // especially during bulk PDF generation.
            id: studentData?.id || studentData?.studentId || marksheet?.studentId,
            studentName:
              studentData?.studentName ||
              studentData?.name ||
              studentData?.firstName + " " + studentData?.lastName ||
              "Unknown Student",
            rollNumber:
              studentData?.rollNumber ||
              studentData?.rollNo ||
              studentData?.roll ||
              "N/A",
            enrolmentNo:
              studentData?.enrolmentNo ||
              studentData?.enrollmentNo ||
              studentData?.enrollmentNumber ||
              "",
            fatherName:
              studentData?.fatherName || studentData?.guardianName || "N/A",
            motherName: studentData?.motherName || "N/A",
            dateOfBirth: studentData?.dateOfBirth || [],
            gender: studentData?.gender || "M",
            medium: studentData?.medium || studentData?.language || "English",
            caste: studentData?.caste || studentData?.category || "GEN",
            section: studentData?.section || "",
            className: marksheet.className || selectedClass?.name || "",
            address: studentData?.address || "",
            phone: studentData?.phone || studentData?.mobileNo || "",
            email: studentData?.email || studentData?.gmail || "",
            photo: studentData?.photo || "",
            admissionNumber: studentData?.admissionNumber || "",
            admissionDate: studentData?.admissionDate || "",
            bloodGroup: studentData?.bloodGroup || "",
            category: studentData?.category || "",
            religion: studentData?.religion || "",
            aadharNo: studentData?.aadharNo || "",
            samagraId:
              studentData?.samagraId ||
              studentData?.samagraID ||
              studentData?.sssmid ||
              "",
          },
          schoolName: schoolData?.nameofSchool || "School",
          schoolCode: schoolData?.schoolCode || "",
          diseCode: schoolData?.diseCode || "",
          block: schoolBlock,
          district: schoolDistrict,
          marksheetType: marksheet.marksheetType || selectedMarksheetType,
          sessionYear: marksheet.sessionYear || selectedSession?.name || "",
          className: marksheet.className || selectedClass?.name || "",
          subjects:
            (marksheet.marksheetType === "FINAL"
              ? buildFinalSubjectsWithExamTotals(marksheet)
              : marksheet.subjects
            )?.map((sub: any) => {
              const theoryMarks =
                sub.theoryMarks ?? sub.obtainedTheoryMarks ?? null;
              const practicalMarks =
                sub.practicalMarks ?? sub.obtainedPracticalMarks ?? null;
              const totalMarks =
                sub.totalMarks ??
                sub.obtainedMarks ??
                (theoryMarks === -1 ? 0 : theoryMarks) +
                (practicalMarks === -1 ? 0 : practicalMarks);

              return {
                subjectCode: sub.subjectCode || sub.subject || sub.code || "",
                subjectName:
                  sub.subjectName ||
                  sub.name ||
                  sub.subject ||
                  sub.subjectName ||
                  "",
                theoryMarks: theoryMarks,
                practicalMarks: practicalMarks,
                quarterlyWeightage: sub.quarterlyWeightage ?? undefined,
                halfYearlyWeightage: sub.halfYearlyWeightage ?? undefined,
                originalPracticalMarks: sub.originalPracticalMarks ?? undefined,
                quarterlyTotal: sub.quarterlyTotal ?? null, // Total from QUARTERLY marksheet
                halfYearlyTotal: sub.halfYearlyTotal ?? null, // Total from HALF_YEARLY marksheet
                quarterlyTheoryMarks: sub.quarterlyTheoryMarks ?? null,
                quarterlyPracticalMarks: sub.quarterlyPracticalMarks ?? null,
                halfYearlyTheoryMarks: sub.halfYearlyTheoryMarks ?? null,
                halfYearlyPracticalMarks: sub.halfYearlyPracticalMarks ?? null,
                groupName: sub.groupName || undefined, // Group name for NSQF/ADDITIONAL GROUP filtering
                totalMarks: totalMarks,
                grade: sub.grade || sub.letterGrade || "",
                remark: sub.remark || sub.remarks || sub.note || "",
              };
            }) || [],
          percentage: marksheet.percentage || 0,
          totalGrade: marksheet.totalGrade || "",
          result: marksheet.result || "",
          rank: marksheet.rank || 0,
          failSubjects: marksheet.failSubjects || "",
          teacherId: getDefaultTeacherId(marksheet.className || selectedClass?.name || ""),
          onPrint: undefined,
          isPdf: false,
          school: schoolData,
          classId: marksheet.classId || selectedClass?.id,
          sessionId: selectedSession?.id,
        };

        // Check for cancellation before rendering
        if (isBulkDownloadCancelledRef.current) {
          break;
        }

        const host = await renderMarksheetOnce(
          {
            ...marksheetData,
            hideDownloadButton: true,
            queryClient: bulkQueryClient,
          },
          MarksheetTemplateWithContext
        );

        // Check for cancellation after rendering
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        // Wait for the photo to either load or fall back, then give layout a short tick.
        await waitForMarksheetPhotoToSettle(host, { timeoutMs: 25000, pollMs: 120 });
        await new Promise((resolve) => setTimeout(resolve, 60));

        // Check for cancellation after delay
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        const canvas = await html2canvas(host, {
          useCORS: true,
          allowTaint: false,
          background: null,
          scale: 1.2,
          // Use offset* to ensure borders are included in capture
          width: host.offsetWidth,
          height: host.offsetHeight,
          windowWidth: host.offsetWidth,
          windowHeight: host.offsetHeight,
          logging: false,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc: Document) => {
            const clonedElement = clonedDoc.body.firstElementChild as HTMLElement;
            if (clonedElement) {
              clonedElement.style.backgroundColor = "#ffffff";
            }
          },
        } as any);

        // Check for cancellation after canvas generation
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        // Use PNG for crisp borders/lines in PDF (JPEG can introduce border artifacts)
        const imgData = canvas.toDataURL("image/png");

        // Inset a bit to avoid PDF edge clipping of the border
        const inset = 0.5;
        const maxWidth = A4_PAGE_WIDTH_MM - (PDF_MARGIN_MM + inset) * 2;
        const maxHeight = A4_PAGE_HEIGHT_MM - (PDF_MARGIN_MM + inset) * 2;

        const naturalWidth = maxWidth;
        const naturalHeight = (canvas.height * naturalWidth) / canvas.width;

        const scale = naturalHeight > maxHeight ? maxHeight / naturalHeight : 1;
        const renderWidth = naturalWidth * scale;
        const renderHeight = naturalHeight * scale;
        const x = (A4_PAGE_WIDTH_MM - renderWidth) / 2;
        const y = PDF_MARGIN_MM + inset;

        // Check for cancellation before adding to PDF
        if (isBulkDownloadCancelledRef.current) {
          removeHost(host);
          break;
        }

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
        pageIndex++;

        removeHost(host);

        setProgress(Math.round(((i + 1) / marksheetsToProcess.length) * 100));
      }

      // Check if cancelled before saving
      if (isBulkDownloadCancelledRef.current) {
        return;
      }

      pdf.save(filename);
      showSuccess(
        t("found_marksheets_for_type")
          .replace("{count}", marksheetsToProcess.length.toString())
          .replace("{type}", selectedMarksheetType)
          .replace("{className}", className)
          .replace("{sessionName}", sessionName)
      );
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      showError(
        t("failed_to_delete_marksheet").replace("{error}", errorMessage)
      );
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setCurrentStudent("");
      isBulkDownloadCancelledRef.current = false;
    }
  };

  const columns = [
    { id: "studentName", label: t("student_name"), minWidth: 130 },
    { id: "rollNumber", label: t("roll_number"), minWidth: 130 },
    { id: "className", label: t("class"), minWidth: 130 },
    { id: "marksheetTypeLabel", label: t("marksheet_type"), minWidth: 130 },
    { id: "monthType", label: t("month_type"), minWidth: 130 },
    { id: "sessionYear", label: t("session_year"), minWidth: 130 },
  ];

  let marksheetsToUse: any[] = [];

  // Helper function to filter marksheets by search term
  const filterMarksheetsBySearch = (marksheets: any[]): any[] => {
    if (!debouncedSearch) {
      return marksheets;
    }

    const searchLower = debouncedSearch.toLowerCase().trim();
    const isNumberSearch = /^\d+$/.test(searchLower);

    return marksheets.filter((row) => {
      const student = row.student as any;
      const studentName =
        student?.studentName ||
        student?.firstName + " " + student?.lastName ||
        "";
      const rollNumber =
        student?.rollNumber || student?.rollNo || student?.roll || "";
      const className = row.className as string || "";

      let matchesSearch = false;

      if (isNumberSearch) {
        // Special handling for class name filtering by number
        const classPattern = new RegExp(`^${searchLower}(st|nd|rd|th)`, 'i');
        matchesSearch = classPattern.test(className.toLowerCase());
      } else {
        // Default search behavior
        matchesSearch = (
          studentName.toLowerCase().includes(searchLower) ||
          rollNumber.toLowerCase().includes(searchLower) ||
          row.marksheetType?.toLowerCase().includes(searchLower) ||
          row.monthType?.toLowerCase().includes(searchLower) ||
          row.sessionYear?.toLowerCase().includes(searchLower) ||
          className.toLowerCase().includes(searchLower)
        );
      }

      return matchesSearch;
    });
  };

  // For students: Prioritize marksheetByStudentCode query if studentCode is available
  const effectiveTableFilterMarksheetType =
    tableFilterMarksheetType === "FINAL" && !isSelectedClass11thForTableFilter
      ? "ANNUAL"
      : tableFilterMarksheetType;

  if (isStudent && studentCode && studentMarksheetByCodeQuery.data) {
    const typeFiltered = effectiveTableFilterMarksheetType
      ? studentMarksheetByCodeQuery.data.filter((row: any) => row.marksheetType === effectiveTableFilterMarksheetType)
      : studentMarksheetByCodeQuery.data;
    marksheetsToUse = filterMarksheetsBySearch(typeFiltered);
  } else if (isStudent && studentMarksheetQuery.data) {
    const typeFiltered = effectiveTableFilterMarksheetType
      ? studentMarksheetQuery.data.filter((row: any) => row.marksheetType === effectiveTableFilterMarksheetType)
      : studentMarksheetQuery.data;
    marksheetsToUse = filterMarksheetsBySearch(typeFiltered);
  } else {
    const pageContent = isServerNameSearchActive
      ? (marksheetsSearchByNameQuery.data?.content || [])
      : (effectiveTableFilterMarksheetType
        ? (marksheetsByTypePageQuery.data?.content || [])
        : (marksheetsPageQuery.data?.content || []));

    // Server already applies class/session/school/type (marksheetsPageQuery / marksheetsByTypePageQuery / search).
    // Extra client filters (schoolId/classId/sessionYear) were dropping valid rows while pagination still used totalElements from the API.
    marksheetsToUse = pageContent.filter((row: any) => {
      const student = row.student as any;

      // Live search box (not submitted server name-search): narrow the current page only.
      if (!isNameSearchActive && debouncedSearch && !isServerNameSearchActive) {
        const searchLower = debouncedSearch.toLowerCase().trim();
        const studentName =
          student?.studentName ||
          student?.firstName + " " + student?.lastName ||
          "";
        const rollNumber =
          student?.rollNumber || student?.rollNo || student?.roll || "";
        const className = row.className as string || "";

        const isNumberSearch = /^\d+$/.test(searchLower);

        if (isNumberSearch) {
          const classPattern = new RegExp(`^${searchLower}(st|nd|rd|th)`, 'i');
          return classPattern.test(className.toLowerCase());
        }

        return (
          studentName.toLowerCase().includes(searchLower) ||
          rollNumber.toLowerCase().includes(searchLower) ||
          row.marksheetType?.toLowerCase().includes(searchLower) ||
          row.monthType?.toLowerCase().includes(searchLower) ||
          row.sessionYear?.toLowerCase().includes(searchLower) ||
          className.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }

  const marksheets = marksheetsToUse;
  const formattedData = marksheets
    .map((row) => {
      const rowData = row as unknown as Record<string, unknown>;

      return {
        ...row,
        marksheetTypeLabel: (() => {
          const type = String(rowData.marksheetType || "");
          const rowClassName = String(rowData.className || "");
          const is11th = rowClassName.toLowerCase().includes("11th");

          if (type === "ANNUAL" && !is11th) {
            return t("final");
          }

          const opt = marksheetTypeOptions.find((o) => o.value === type);
          return opt?.label || type || t("not_available");
        })(),
        studentName: (() => {
          const student = rowData.student as Record<string, unknown> | undefined;

          if (student?.studentName) {
            return toText(student.studentName);
          } else if (student?.firstName && student?.lastName) {
            return `${toText(student.firstName)} ${toText(student.lastName)}`.trim();
          } else if (student?.firstName) {
            return toText(student.firstName);
          }

          const studentId = (rowData.studentId as number) ?? (student?.id as number);
          if (studentId) {
            const fallbackName = getStudentName(studentId);
            if (fallbackName === studentId) {
              return t("unknown_student");
            }
            return fallbackName;
          }

          return t("unknown_student");
        })(),
        rollNumber: (() => {
          const student = rowData.student as Record<string, unknown> | undefined;

          if (student?.rollNumber) {
            return toText(student.rollNumber);
          } else if (student?.rollNo) {
            return toText(student.rollNo);
          } else if (student?.roll) {
            return toText(student.roll);
          } else {
            return t("not_available");
          }
        })(),
        className: (() => {
          const className = rowData.className as string | undefined;
          return className || t("not_available");
        })(),
        monthType: (() => {
          const monthType = rowData.monthType as string | undefined;
          if (monthType) {
            const monthOption = monthTypeOptions.find(
              (option) => option.value === monthType
            );
            return monthOption ? monthOption.label : monthType;
          }
          return t("not_available");
        })(),
        // Preserve original monthType value for internal filtering (e.g. bulk publish dialog)
        monthTypeValue: (rowData.monthType as string | undefined) || "",
      };
    })
    .sort((a, b) => {
      const nameA = String(a.studentName || "").toLowerCase();
      const nameB = String(b.studentName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

  // When we apply client-side filters (e.g. marksheet type dropdown or live search),
  // the server `totalElements` no longer matches the visible rows, so pagination count must use filtered length.
  const totalElementsForTable = useMemo(() => {
    if (isStudent) return formattedData.length;

    // Name-search mode is server-side; backend totalElements is correct.
    if (isServerNameSearchActive) {
      return marksheetsSearchByNameQuery.data?.totalElements ?? formattedData.length;
    }

    const hasClientSideFilters = Boolean(effectiveTableFilterMarksheetType) || Boolean(debouncedSearch);
    if (hasClientSideFilters) return formattedData.length;

    return marksheetsPageQuery.data?.totalElements ?? formattedData.length;
  }, [
    isStudent,
    formattedData.length,
    isServerNameSearchActive,
    marksheetsSearchByNameQuery.data?.totalElements,
    effectiveTableFilterMarksheetType,
    debouncedSearch,
    marksheetsPageQuery.data?.totalElements,
  ]);

  const isBoardClass =
    selectedClass &&
    (selectedClass.name === "10th" || selectedClass.name === "12th");

  let filteredMarksheetTypeOptions: { value: string; label: string }[] = [];
  if (isBoardClass) {
    filteredMarksheetTypeOptions = marksheetTypeOptions.filter(
      (opt) => opt.value !== "ANNUAL"
    );
  } else if (selectedClass?.name) {
    filteredMarksheetTypeOptions = marksheetTypeOptions.filter(
      (opt) => opt.value !== "BOARD" && opt.value !== "PRE_BOARD"
    );
  } else {
    filteredMarksheetTypeOptions = marksheetTypeOptions;
  }

  const selectedClassNameForDownload = String(selectedClass?.name ?? "").toLowerCase();
  const isClass3rd4th6th7thForDownload =
    selectedClassNameForDownload.includes("3rd") ||
    selectedClassNameForDownload.includes("4th") ||
    selectedClassNameForDownload.includes("6th") ||
    selectedClassNameForDownload.includes("7th");

  const downloadMarksheetTypeOptions: { value: string; label: string }[] = isClass3rd4th6th7thForDownload
    ? [
        ...marksheetTypeOptions,
        { value: "SAHSHEKSHIK", label: "Sahshekshik" },
      ]
    : marksheetTypeOptions;

  const getStudentDisplayName = (student: any): string => {
    if (student.studentName) {
      return student.studentName;
    } else if (student.firstName && student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    } else if (student.firstName) {
      return student.firstName;
    } else if (student.lastName) {
      return student.lastName;
    } else {
      return 'Unknown Student';
    }
  };

  const getStudentClassId = (student: any): number => {
    return student.schoolClassId || student.classId || 0;
  };

  const marksEntriesMap = useMemo(() => {
    const map: Map<number, Set<string>> = new Map();
    const effectiveMonthType =
      viewMarksEntriesMarksheetType === "MONTHLY" ? viewMarksEntriesMonthType : "NA";

    if (marksEntriesQuery.data) {
      marksEntriesQuery.data.forEach((entry) => {
        if (viewMarksEntriesMarksheetType && entry.marksheetType !== viewMarksEntriesMarksheetType) {
          return;
        }

        if (effectiveMonthType && entry.monthType !== effectiveMonthType) {
          return;
        }

        entry.subjectMarksDtoList?.forEach((subjectMark) => {
          if (!map.has(subjectMark.studentId)) {
            map.set(subjectMark.studentId, new Set());
          }
          map.get(subjectMark.studentId)?.add(subjectMark.subjectCode);
        });
      });
    }

    return map;
  }, [marksEntriesQuery.data, viewMarksEntriesMarksheetType, viewMarksEntriesMonthType]);

  const marksEntriesDataMap = useMemo(() => {
    const map: Map<number, Map<string, { theory: number; practical: number; marksheetType: string; monthType: string }>> = new Map();
    const effectiveMonthType =
      viewMarksEntriesMarksheetType === "MONTHLY" ? viewMarksEntriesMonthType : "NA";

    if (marksEntriesQuery.data) {
      marksEntriesQuery.data.forEach((entry) => {
        if (viewMarksEntriesMarksheetType && entry.marksheetType !== viewMarksEntriesMarksheetType) {
          return;
        }

        if (effectiveMonthType && entry.monthType !== effectiveMonthType) {
          return;
        }

        entry.subjectMarksDtoList?.forEach((subjectMark) => {
          if (!map.has(subjectMark.studentId)) {
            map.set(subjectMark.studentId, new Map());
          }
          map.get(subjectMark.studentId)?.set(subjectMark.subjectCode, {
            theory: subjectMark.theory,
            practical: subjectMark.practical,
            marksheetType: entry.marksheetType,
            monthType: entry.monthType,
          });
        });
      });
    }

    return map;
  }, [marksEntriesQuery.data, viewMarksEntriesMarksheetType, viewMarksEntriesMonthType]);

  const filteredStudentsForStatus = useMemo(() => {
    if (!selectedClass?.id || !selectedSession?.id) {
      return [];
    }

    // If both session and class are selected, use the filtered API results
    if (selectedSession?.id && selectedClass?.id) {
      return (filteredStudentsFromAPI || [])
        .filter((student: any) => student.status !== false)
        .sort((a: any, b: any) => {
          const nameA = getStudentDisplayName(a);
          const nameB = getStudentDisplayName(b);
          return nameA.localeCompare(nameB);
        });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    let studentsToFilter = studentsByClassQuery.data || [];
    if (studentsToFilter.length === 0 && studentsQuery.data) {
      studentsToFilter = studentsQuery.data;
    }

    const students = studentsToFilter.filter((student: any) => {
      const studentClassId = getStudentClassId(student);
      const classMatch = studentClassId === selectedClass.id;
      const sessionMatch = student.sessionId === selectedSession.id;
      const isActive = student.status !== false;
      return classMatch && sessionMatch && isActive;
    });

    students.sort((a: any, b: any) => {
      const nameA = getStudentDisplayName(a);
      const nameB = getStudentDisplayName(b);
      return nameA.localeCompare(nameB);
    });

    return students;
  }, [
    filteredStudentsFromAPI,
    studentsByClassQuery.data,
    studentsQuery.data,
    selectedClass?.id,
    selectedSession?.id
  ]);

  const hasMarksForSubject = (studentId: number, subjectCode: string): boolean => {
    return marksEntriesMap.get(studentId)?.has(subjectCode) ?? false;
  };

  const hasAllMarksForStudent = (studentId: number): boolean => {
    const student = filteredStudentsForStatus.find((s: any) => s.id === studentId);
    if (!student || !subjectsEnrolledByStudents.length) return false;

    let studentSubjectCodes: string[] = [];
    if (student.subjects) {
      if (Array.isArray(student.subjects)) {
        studentSubjectCodes = student.subjects;
      } else if (typeof student.subjects === 'string') {
        studentSubjectCodes = student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
    }

    return studentSubjectCodes.length > 0 &&
      studentSubjectCodes.every((code: string) => hasMarksForSubject(studentId, code));
  };

  const getMarksEntryForStudent = (studentId: number): { marksheetType: string; monthType: string; subjectMarks: any[] } | null => {
    const studentMarksMap = marksEntriesDataMap.get(studentId);
    if (!studentMarksMap || studentMarksMap.size === 0) return null;

    const student = filteredStudentsForStatus.find((s: any) => s.id === studentId);
    if (!student) return null;

    let studentSubjectCodes: string[] = [];
    if (student.subjects) {
      if (Array.isArray(student.subjects)) {
        studentSubjectCodes = student.subjects;
      } else if (typeof student.subjects === 'string') {
        studentSubjectCodes = student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
    }

    const subjectMarks: any[] = [];
    let marksheetType = '';
    let monthType = '';

    studentSubjectCodes.forEach((code: string) => {
      const marksData = studentMarksMap.get(code);
      if (marksData) {
        subjectMarks.push({
          subjectCode: code,
          theory: marksData.theory,
          practical: marksData.practical,
        });
        if (!marksheetType) {
          marksheetType = marksData.marksheetType;
          monthType = marksData.monthType;
        }
      }
    });

    if (subjectMarks.length === 0 || !marksheetType || !monthType) return null;

    return { marksheetType, monthType, subjectMarks };
  };

  const handleCreateMarksheetFromEntry = async (studentId: number) => {
    if (!selectedClass || !selectedSession) {
      showError(t("please_select_both_class_and_session"));
      return;
    }

    const marksEntryData = getMarksEntryForStudent(studentId);
    if (!marksEntryData) {
      showError(t("no_marks_found_for_student"));
      return;
    }

    const student = filteredStudentsForStatus.find((s: any) => s.id === studentId);
    if (!student) {
      showError(t("student_not_found"));
      return;
    }

    const subjectsWithNames = marksEntryData.subjectMarks.map((mark) => {
      const subject = classSubjects?.find((s) => s.subjectCode === mark.subjectCode);
      return {
        subjectCode: mark.subjectCode,
        subjectName: subject?.name || mark.subjectCode,
        theoryMarks: mark.theory,
        practicalMarks: mark.practical,
      };
    });

    const marksheetData: MarksheetFormData = {
      studentId: studentId,
      schoolId: schoolId,
      classId: selectedClass.id!,
      marksheetType: marksEntryData.marksheetType,
      sessionYear: selectedSession.name || "",
      className: selectedClass.name || "",
      monthType: marksEntryData.monthType,
      subjects: subjectsWithNames,
    };

    const processedData = {
      ...marksheetData,
      sessionId: selectedSession.id!,
      subjects: marksheetData.subjects.map((subject) => {
        const theoryMarks = subject.theoryMarks === null ? -1 : Number(subject.theoryMarks);
        const practicalMarks = subject.practicalMarks === null ? -1 : Number(subject.practicalMarks);

        return {
          subjectCode: subject.subjectCode,
          subjectName: subject.subjectName,
          theoryMarks,
          practicalMarks,
          originalPracticalMarks: practicalMarks !== -1 ? practicalMarks : undefined,
        };
      }),
    };

    createMarksheetMutation.mutate(processedData, {
      onSuccess: async () => {
        // Do NOT delete marks-entry after marksheet creation.
        // Backend validation relies on marks-entry existing to prevent re-entry.
        showSuccess(t("marksheet_created_successfully"));
        marksEntriesQuery.refetch();
      },
      onError: (error: any) => {
        showError(
          t("failed_to_create_marksheet").replace(
            "{error}",
            error?.response?.data?.message || error?.message || "Unknown error"
          )
        );
      },
    });
  };

  const subjectsEnrolledByStudents = useMemo(() => {
    const subjectCodesSet = new Set<string>();

    filteredStudentsForStatus.forEach((student: any) => {
      if (student.subjects) {
        let studentSubjectCodes: string[] = [];
        if (Array.isArray(student.subjects)) {
          studentSubjectCodes = student.subjects;
        } else if (typeof student.subjects === 'string') {
          studentSubjectCodes = student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
        }

        studentSubjectCodes.forEach((code: string) => {
          subjectCodesSet.add(code);
        });
      }
    });

    if (!classSubjects || classSubjects.length === 0) {
      return [];
    }

    return classSubjects.filter((subject) =>
      subjectCodesSet.has(subject.subjectCode)
    );
  }, [filteredStudentsForStatus, classSubjects]);

  const getRelatedTermMarksheets = (finalRow: any) => {
  const allMarksheets =
    marksheetsByClassIdAndSessionIdQuery.data ||
    marksheetsQuery.data ||
    marksheetsPageQuery.data?.content ||
    [];

    const finalStudentCode =
      finalRow.student?.studentCode ?? finalRow.studentCode ?? "";
    const finalStudentId =
      finalRow.student?.id ?? finalRow.studentId ?? null;

    const matches = (ms: any, type: string) => {
      if (ms.marksheetType !== type) return false;
      if (ms.schoolId !== finalRow.schoolId) return false;
      if (ms.classId !== finalRow.classId) return false;
      if (
        finalRow.sessionId != null &&
        ms.sessionId != null &&
        ms.sessionId !== finalRow.sessionId
      ) {
        return false;
      }
      if (!finalRow.sessionId && ms.sessionYear !== finalRow.sessionYear) return false;

      const msStudentCode = ms.student?.studentCode ?? ms.studentCode ?? "";
      const msStudentId = ms.student?.id ?? ms.studentId ?? null;

      if (finalStudentCode && msStudentCode) {
        return msStudentCode === finalStudentCode;
      }
      if (finalStudentId && msStudentId) {
        return msStudentId === finalStudentId;
      }
      return false;
    };

    const quarterly = allMarksheets.find((ms: any) =>
      matches(ms, "QUARTERLY")
    );
    const halfYearly = allMarksheets.find((ms: any) =>
      matches(ms, "HALF_YEARLY")
    );

    return { quarterly, halfYearly };
  };

  const buildFinalSubjectsWithExamTotals = (finalRow: any) => {
    const { quarterly, halfYearly } = getRelatedTermMarksheets(finalRow);

    const quarterlySubjects: any[] = quarterly?.subjects || [];
    const halfYearlySubjects: any[] = halfYearly?.subjects || [];

    return (finalRow.subjects || []).map((sub: any) => {
      const qSub = quarterlySubjects.find(
        (s: any) => s.subjectCode === sub.subjectCode
      );
      const hSub = halfYearlySubjects.find(
        (s: any) => s.subjectCode === sub.subjectCode
      );

      const quarterlyTotal =
        qSub && qSub.theoryMarks != null && qSub.practicalMarks != null
          ? qSub.theoryMarks === -1 || qSub.practicalMarks === -1
            ? null
            : (Number(qSub.theoryMarks) || 0) +
            (Number(qSub.practicalMarks) || 0)
          : undefined;

      const halfYearlyTotal =
        hSub && hSub.theoryMarks != null && hSub.practicalMarks != null
          ? hSub.theoryMarks === -1 || hSub.practicalMarks === -1
            ? null
            : (Number(hSub.theoryMarks) || 0) +
            (Number(hSub.practicalMarks) || 0)
          : undefined;

      return {
        ...sub,
        // Preserve original weightage values from FINAL marksheet (for 5% columns)
        // Add full totals as separate properties (for QUARTERLY TOTAL and HALF YEARLY TOTAL columns)
        quarterlyTotal: quarterlyTotal !== undefined ? quarterlyTotal : null,
        halfYearlyTotal: halfYearlyTotal !== undefined ? halfYearlyTotal : null,
        // Add individual marks from quarterly and half-yearly marksheets (for 11th class template)
        quarterlyTheoryMarks: qSub?.theoryMarks != null ? qSub.theoryMarks : null,
        quarterlyPracticalMarks: qSub?.practicalMarks != null ? qSub.practicalMarks : null,
        halfYearlyTheoryMarks: hSub?.theoryMarks != null ? hSub.theoryMarks : null,
        halfYearlyPracticalMarks: hSub?.practicalMarks != null ? hSub.practicalMarks : null,
        // Keep original quarterlyWeightage and halfYearlyWeightage from FINAL marksheet
      };
    });
  };

  const renderActions = (row: any) => (
    <IconButton
      onClick={(e) => handleMenuOpen(e, row)}
      title={t("actions")}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const bulkPublishCandidates = useMemo(() => {
    // Use the same formatted rows used in the main table, but allow optional filtering here.
    const rows = formattedData || [];

    return rows.filter((row: any) => {
      if (bulkPublishMarksheetType && row.marksheetType !== bulkPublishMarksheetType) return false;
      // Month type filter should only apply for MONTHLY marksheets
      if (
        bulkPublishMarksheetType === "MONTHLY" &&
        bulkPublishMonthType &&
        row.monthTypeValue !== bulkPublishMonthType
      ) {
        return false;
      }
      return true;
    });
  }, [formattedData, bulkPublishMarksheetType, bulkPublishMonthType]);

  const bulkSelectedCount = bulkSelectedMarksheetIds.size;
  const bulkAllSelected =
    bulkPublishCandidates.length > 0 &&
    bulkPublishCandidates.every((r: any) => bulkSelectedMarksheetIds.has(r.id));

  const setBulkSelectAll = (checked: boolean) => {
    if (!checked) {
      setBulkSelectedMarksheetIds(new Set());
      return;
    }
    const next = new Set<number>();
    bulkPublishCandidates.forEach((r: any) => {
      if (typeof r.id === "number") next.add(r.id);
    });
    setBulkSelectedMarksheetIds(next);
  };

  const handleBulkPublishSubmit = async () => {
    if (bulkSelectedMarksheetIds.size === 0) {
      showError(t("please_select_at_least_one_marksheet") || "Please select at least one marksheet");
      return;
    }

    const selectedRows = bulkPublishCandidates.filter((r: any) => bulkSelectedMarksheetIds.has(r.id));
    const missingStudentCode = selectedRows.filter((r: any) => !r?.student?.studentCode);
    if (missingStudentCode.length > 0) {
      showError(
        (t("some_selected_marksheets_missing_student_code") as any) ||
          `Some selected marksheets are missing student code (${missingStudentCode.length})`
      );
      return;
    }

    const published = bulkPublishAction === "PUBLISH";
    const payload = selectedRows.map((r: any) => ({
      marksheetId: r.id,
      studentCode: r.student.studentCode,
      published,
    }));

    try {
      await publishBulkMarksheetsMutation.mutateAsync(payload as any);

      // Update marksheets cache immediately so the modal/table reflects new publish state without reload
      queryClient.setQueryData(
        ["marksheets", selectedClass?.id ?? null, selectedSession?.id ?? null],
        (oldData: any) => {
          if (!Array.isArray(oldData)) return oldData;
          const ids = new Set<number>(selectedRows.map((r: any) => r.id));
          return oldData.map((ms: any) =>
            ids.has(ms?.id) ? { ...ms, published } : ms
          );
        }
      );

      showSuccess(
        published
          ? t("marksheets_published_successfully") || "Marksheets published successfully"
          : t("marksheets_unpublished_successfully") || "Marksheets unpublished successfully"
      );
      // Keep dialog open but clear selection so user can continue bulk operations
      setBulkSelectedMarksheetIds(new Set());
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || "Failed to update publish status");
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: "auto", mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("marksheet_management")}
          </Typography>
          {!isStudent && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#6c63ff",
                    "&:hover": { backgroundColor: "#574fe0" },
                  }}
                  onClick={openBulkPublishDialog}
                  disabled={!selectedClass || !selectedSession}
                  startIcon={<PublishIcon />}
                >
                  {t("bulk_publish_unpublish") || "Bulk Publish/Unpublish"}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" },
                  }}
                  onClick={handleOpenImportModal}
                  disabled={!selectedClass || !selectedSession}
                  startIcon={<FileUploadIcon />}
                >
                  {t("upload_excel")}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" },
                  }}
                  onClick={handleOpenBulkModal}
                >
                  {t("bulk_download_marksheets")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleDownloadMarksheet}
                  disabled={
                    downloadMarksheetMutation.isLoading ||
                    !selectedClass ||
                    !selectedSession ||
                    !importMarksheetType
                  }
                  startIcon={
                    downloadMarksheetMutation.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <FileDownloadIcon />
                    )
                  }
                >
                  {downloadMarksheetMutation.isLoading
                    ? t("downloading")
                    : t("marksheet_excel")}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#3092e3",
                    "&:hover": { backgroundColor: "primary.main" },
                  }}
                  onClick={() => {
                    setIsEdit(false);
                    setCurrentMarksheet(undefined);
                    setModalOpen(true);
                  }}
                >
                  {t("add_marksheet")}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#28a745",
                    "&:hover": { backgroundColor: "#218838" },
                  }}
                  onClick={() => {
                    setMarksEntryModalOpen(true);
                  }}
                  disabled={!selectedClass || !selectedSession}
                >
                  {t("add_marks_entry")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}></Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              fullWidth
              select
              label={t("marksheet_type")}
              name="marksheetType"
              value={tableFilterMarksheetType}
              onChange={(e) => setTableFilterMarksheetType(e.target.value)}
              size="small"
              margin="dense"
              sx={{ backgroundColor: "#f8f9fb", minWidth: 150 }}
              disabled={false}
              InputLabelProps={{
                shrink: Boolean(tableFilterMarksheetType === "" || tableFilterMarksheetType),
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value || value === "") {
                    return t("All");
                  }
                  const selectedOption = filteredMarksheetTypeOptions.find(
                    (opt) => opt.value === value
                  );
                  if (selectedOption) return selectedOption.label;
                  return typeof value === "string" ? value : "";
                },
              }}
            >
              <MenuItem value="">{t("All Types")}</MenuItem>
              {filteredMarksheetTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder={t("search")}
              value={searchTerm}
              onChange={(e) => {
                const next = e.target.value;
                setSearchTerm(next);
                if (!next.trim()) {
                  setSubmittedSearchName("");
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const name = searchTerm.trim();
                  setSubmittedSearchName(name);
                  setTablePage(0);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
          </Box>
        </Box>

        {(isStudent
          ? (studentCode ? studentMarksheetByCodeQuery.isLoading : studentMarksheetQuery.isLoading) || loadingStudentByMobile
          : (isServerNameSearchActive
            ? marksheetsSearchByNameQuery.isLoading
            : (effectiveTableFilterMarksheetType
              ? marksheetsByTypePageQuery.isLoading
              : marksheetsPageQuery.isLoading))) && (
          <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
        )}
        {(() => {
          const error = isStudent
            ? (studentCode ? studentMarksheetByCodeQuery.error : studentMarksheetQuery.error)
            : (isServerNameSearchActive
              ? marksheetsSearchByNameQuery.error
              : (effectiveTableFilterMarksheetType
                ? marksheetsByTypePageQuery.error
                : marksheetsPageQuery.error));
          if (!error) return null;

          const errorMsg = (error as any)?.response?.data?.message || (error as any)?.message || String(error);
          const isNotPublished = errorMsg === "Marksheet not published yet";

          return (
            <Alert severity={isNotPublished ? "info" : "error"} sx={{ mb: 2 }}>
              {isNotPublished ? t("marksheet_not_published_yet") : errorMsg}
            </Alert>
          );
        })()}
        {isStudent && (studentCode ? studentMarksheetByCodeQuery.data : studentMarksheetQuery.data) && formattedData.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("no_marksheet_found")}
          </Alert>
        )}
        {!isStudent && tableFilterMarksheetType && formattedData.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("no_marksheets_found_for_type").replace(
              "{type}",
              tableFilterMarksheetType
            )}
          </Alert>
        )}

        <EnhancedTable
          columns={columns}
          data={formattedData}
          searchFields={[]}
          actions={renderActions}
          enablePagination
          page={tablePage}
          rowsPerPage={tableRowsPerPage}
          loading={
            !isStudent &&
            (isServerNameSearchActive
              ? (marksheetsSearchByNameQuery.isLoading || marksheetsSearchByNameQuery.isFetching)
              : (effectiveTableFilterMarksheetType
                ? (marksheetsByTypePageQuery.isLoading || marksheetsByTypePageQuery.isFetching)
                : (marksheetsPageQuery.isLoading || marksheetsPageQuery.isFetching)))
          }
          totalElements={
            isStudent
              ? formattedData.length
              : (isServerNameSearchActive
                ? (marksheetsSearchByNameQuery.data?.totalElements ?? formattedData.length)
                : (effectiveTableFilterMarksheetType
                  ? (marksheetsByTypePageQuery.data?.totalElements ?? formattedData.length)
                  : (marksheetsPageQuery.data?.totalElements ?? formattedData.length)))
          }
          onPageChange={(_, newPage) => setTablePage(newPage)}
          onRowsPerPageChange={(e) => {
            const next = Number.parseInt(e.target.value, 10);
            setTableRowsPerPage(Number.isFinite(next) && next > 0 ? next : 10);
            setTablePage(0);
          }}
          cardProps={{
            elevation: 0,
            sx: {
              border: "none",
              boxShadow: "none",
              backgroundColor: "transparent",
            },
          }}
        />
      </Paper>

      <MarkSheetFormModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        initialData={currentMarksheet}
        schoolCode={schoolData?.schoolCode ?? ""}
        onSubmit={async (data: MarksheetFormData) => {
          const processedData = {
            ...data,
            sessionId: selectedSession?.id ?? 0,
            monthType: data.monthType || DEFAULT_MONTH_TYPE,
            subjects: data.subjects.map((subject: any) => {
              const theoryMarksValue =
                subject.theoryMarks === null || subject.theoryMarks === ""
                  ? -1
                  : Number(subject.theoryMarks);
              const practicalMarksValue =
                subject.practicalMarks === null || subject.practicalMarks === ""
                  ? -1
                  : Number(subject.practicalMarks);
              const hasExplicitOrig =
                subject.originalPracticalMarks !== undefined &&
                subject.originalPracticalMarks !== null &&
                subject.originalPracticalMarks !== "";
              const originalPracticalResolved = hasExplicitOrig
                ? Number(subject.originalPracticalMarks)
                : practicalMarksValue;
              return {
                subjectCode: subject.subjectCode,
                subjectName: subject.subjectName,
                theoryMarks: theoryMarksValue,
                practicalMarks: practicalMarksValue,
                originalPracticalMarks:
                  originalPracticalResolved !== -1 &&
                  !Number.isNaN(originalPracticalResolved)
                    ? originalPracticalResolved
                    : undefined,
              };
            }),
          };

          try {
            if (isEdit) {
              await updateMarksheetMutation.mutateAsync(processedData);
              showSuccess(t("marksheet_updated_successfully"));
            } else {
              await createMarksheetMutation.mutateAsync(processedData);
              showSuccess(t("marksheet_created_successfully"));
            }
            setModalOpen(false);
          } catch (error: any) {
            const message =
              error?.response?.data?.message || error?.message || "Unknown error";
            showError(
              (isEdit ? t("failed_to_update_marksheet") : t("failed_to_create_marksheet")).replace(
                "{error}",
                message
              )
            );
          }
        }}
        isEdit={isEdit}
      />

      <MarksEntryFormModal
        open={marksEntryModalOpen}
        handleClose={() => setMarksEntryModalOpen(false)}
        marksEntries={marksEntriesQuery.data || []}
        onViewMarksEntries={() => setViewMarksEntriesModalOpen(true)}
        onSubmit={async (data: MarksEntryFormData) => {
          try {
            if (!data.subjectMarksDtoList || data.subjectMarksDtoList.length === 0) {
              showError(t("no_marks_to_submit"));
              return;
            }

            const entryData = {
              marksheetType: data.marksheetType,
              monthType: data.monthType,
              classId: data.classId,
              sessionId: data.sessionId,
              subjectMarksDtoList: data.subjectMarksDtoList,
            };

            const response = await addMarksEntryMutation.mutateAsync(entryData);

            const savedCount = response.saved?.length || 0;
            const errorCount = response.errors?.length || 0;

            if (errorCount === 0 && savedCount > 0) {
              showSuccess(
                t("marks_entry_added_successfully").replace("{count}", savedCount.toString())
              );
              setMarksEntryModalOpen(false);
              if (viewMarksEntriesModalOpen) {
                marksEntriesQuery.refetch();
              }
            } else if (savedCount > 0 && errorCount > 0) {
              showSuccess(
                t("marks_entry_added_successfully").replace("{count}", savedCount.toString())
              );
              const errorMessages = response.errors.join("; ");
              showSuccess(
                t("marks_entry_partially_added")
                  .replace("{saved}", savedCount.toString())
              );
              if (viewMarksEntriesModalOpen) {
                marksEntriesQuery.refetch();
              }
            } else if (errorCount > 0) {
              const errorMessages = response.errors.join("; ");
              showError(
                t("marks_entry_failed").replace("{errors}", errorMessages)
              );
            } else {
              showError(t("no_marks_saved"));
            }
          } catch (error: any) {
            showError(
              t("failed_to_add_marks_entry").replace(
                "{error}",
                error?.response?.data?.message || error?.message || "Unknown error"
              )
            );
          }
        }}
      />

      <Dialog
        open={marksheetViewDialogOpen}
        onClose={handleCloseMarksheetView}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("view_marksheet")} -{" "}
            {selectedMarksheetForView?.studentName || "Unknown Student"}
          </Typography>
          <IconButton onClick={handleCloseMarksheetView} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "auto",
            // Mobile: allow horizontal swipe so left/right spacing stays visible.
            overflowX: { xs: "auto", md: "visible" },
            WebkitOverflowScrolling: "touch",
            px: { xs: 1.5, md: 0 },
          }}>
          {selectedMarksheetForView && (
            <Box sx={{ height: "100%", overflow: "auto" }}>
              <MarksheetTemplateWithContext
                key={`marksheet-${getDefaultTeacherId(selectedMarksheetForView.className || "")}-${schoolId}-${teachersData?.length || 0
                  }`}
                classId={selectedMarksheetForView.classId || selectedClass?.id}
                sessionId={selectedSession?.id}
                student={{
                  id:
                    selectedMarksheetForView.student?.id ||
                    selectedMarksheetForView.studentId,
                  rollNumber:
                    selectedMarksheetForView.rollNumber ||
                    selectedMarksheetForView.student?.rollNumber ||
                    "",
                  enrolmentNo:
                    selectedMarksheetForView.student?.enrolmentNo || "",
                  studentName:
                    selectedMarksheetForView.studentName ||
                    selectedMarksheetForView.student?.studentName,
                  firstName: selectedMarksheetForView.student?.firstName || "",
                  lastName: selectedMarksheetForView.student?.lastName || "",
                  fatherName:
                    selectedMarksheetForView.student?.fatherName || "",
                  motherName:
                    selectedMarksheetForView.student?.motherName || "",
                  dateOfBirth:
                    selectedMarksheetForView.student?.dateOfBirth || [],
                  gender: selectedMarksheetForView.student?.gender || "",
                  medium: selectedMarksheetForView.student?.medium || "",
                  caste: selectedMarksheetForView.student?.caste || "",
                  section: selectedMarksheetForView.student?.section || "",
                  className: selectedMarksheetForView.className || "",
                  photo: selectedMarksheetForView.student?.photo || "",
                  samagraId:
                    selectedMarksheetForView.student?.samagraId ||
                    selectedMarksheetForView.student?.samagraID ||
                    selectedMarksheetForView.student?.sssmid ||
                    "",
                  aadharNo:
                    selectedMarksheetForView.student?.aadharNo ||
                    selectedMarksheetForView.student?.aadhaarNo ||
                    selectedMarksheetForView.student?.aadhar ||
                    "",
                }}
                schoolName={
                  schoolData?.nameofSchool ||
                  selectedMarksheetForView.schoolName ||
                  t("school")
                }
                schoolCode={schoolData?.schoolCode || ""}
                diseCode={schoolData?.diseCode || ""}
                block={schoolBlock}
                district={schoolDistrict}
                marksheetType={selectedMarksheetForView.marksheetType || ""}
                sessionYear={selectedMarksheetForView.sessionYear || ""}
                className={selectedMarksheetForView.className || ""}
                subjects={
                  (selectedMarksheetForView.marksheetType === "FINAL"
                    ? buildFinalSubjectsWithExamTotals(selectedMarksheetForView)
                    : selectedMarksheetForView.subjects || []
                  ).map((sub: any) => ({
                    subjectCode: sub.subjectCode || sub.subject || "",
                    subjectName: sub.subjectName || sub.subject || "",
                    theoryMarks: sub.theoryMarks ?? null,
                    practicalMarks: sub.practicalMarks ?? null,
                    quarterlyWeightage: sub.quarterlyWeightage ?? undefined,
                    halfYearlyWeightage: sub.halfYearlyWeightage ?? undefined,
                    originalPracticalMarks: sub.originalPracticalMarks ?? undefined,
                    quarterlyTotal: sub.quarterlyTotal ?? null, // Total from QUARTERLY marksheet
                    halfYearlyTotal: sub.halfYearlyTotal ?? null, // Total from HALF_YEARLY marksheet
                    quarterlyTheoryMarks: sub.quarterlyTheoryMarks ?? null,
                    quarterlyPracticalMarks: sub.quarterlyPracticalMarks ?? null,
                    halfYearlyTheoryMarks: sub.halfYearlyTheoryMarks ?? null,
                    halfYearlyPracticalMarks: sub.halfYearlyPracticalMarks ?? null,
                    groupName: sub.groupName || undefined, // Group name for NSQF/ADDITIONAL GROUP filtering
                    totalMarks:
                      sub.totalMarks ??
                      (sub.theoryMarks === -1 ? 0 : sub.theoryMarks) +
                      (sub.practicalMarks === -1 ? 0 : sub.practicalMarks),
                    grade: sub.grade || "",
                    remark: sub.remark || sub.remarks || "",
                  }))
                }
                percentage={selectedMarksheetForView.percentage || 0}
                totalGrade={selectedMarksheetForView.totalGrade || ""}
                result={selectedMarksheetForView.result || ""}
                rank={selectedMarksheetForView.rank || 0}
                failSubjects={selectedMarksheetForView.failSubjects || ""}
                teacherId={getDefaultTeacherId(selectedMarksheetForView.className || "")}
                onPrint={undefined}
                isPdf={false}
                school={schoolData}
                totalTheoryObtained={selectedMarksheetForView.totalTheoryObtained ?? selectedMarksheetForView.totalTheory ?? undefined}
                totalPracticalObtained={selectedMarksheetForView.totalPracticalObtained ?? selectedMarksheetForView.totalPractical ?? undefined}
                totalObtained={selectedMarksheetForView.totalObtained ?? selectedMarksheetForView.totalMarks ?? undefined}
                marksheet_assessment={selectedMarksheetForView.marksheet_assessment || {}}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Custom Template Marksheet Preview Dialog ── */}
      <Dialog
        open={customMarksheetDialogOpen}
        onClose={() => { setCustomMarksheetDialogOpen(false); setCustomMarksheetPreviewData(null); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "90vh", height: "90vh" },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("view_marksheet")} -{" "}
            {customMarksheetPreviewData?.studentName || ""}
          </Typography>
          <IconButton
            onClick={() => { setCustomMarksheetDialogOpen(false); setCustomMarksheetPreviewData(null); }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {customMarksheetPreviewData && (
            <iframe
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><style>
                ${customMarksheetPreviewData.css}
                @page { size: A4; margin: 12mm; }
                @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
                .ms-photo-box { overflow: hidden; }
                .ms-student-photo { width: 130px; height: 155px; max-width: 130px; max-height: 155px; object-fit: cover; display: block; }
                .ms-photo-box .ms-student-photo { width: 100%; height: 100%; max-width: unset; max-height: unset; }
              </style></head><body>${customMarksheetPreviewData.html}</body></html>`}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Custom Marksheet Preview"
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={viewMarksEntriesModalOpen}
        onClose={() => {
          setViewMarksEntriesModalOpen(false);
          setViewMarksEntriesMarksheetType(DEFAULT_MARKSHEET_TYPE);
          setViewMarksEntriesMonthType("NA");
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <SubjectIcon sx={{ color: "#3092e3" }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                {t("marks_entries_status")}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                setViewMarksEntriesModalOpen(false);
                setViewMarksEntriesMarksheetType(DEFAULT_MARKSHEET_TYPE);
                setViewMarksEntriesMonthType("NA");
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t("marksheet_type")}</InputLabel>
                <Select
                  value={viewMarksEntriesMarksheetType}
                  onChange={(e) => {
                    const nextType = e.target.value as string;
                    setViewMarksEntriesMarksheetType(nextType);
                    if (nextType !== "MONTHLY") {
                      setViewMarksEntriesMonthType("NA");
                    } else if (!viewMarksEntriesMonthType || viewMarksEntriesMonthType === "NA") {
                      setViewMarksEntriesMonthType(DEFAULT_MONTH_TYPE);
                    }
                  }}
                  label={t("marksheet_type")}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                >
                  {marksheetTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {viewMarksEntriesMarksheetType === "MONTHLY" ? (
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>{t("month_type")}</InputLabel>
                  <Select
                    value={viewMarksEntriesMonthType || DEFAULT_MONTH_TYPE}
                    onChange={(e) => setViewMarksEntriesMonthType(e.target.value)}
                    label={t("month_type")}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  >
                    {monthTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          </Box>

          {marksEntriesQuery.isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : marksEntriesQuery.isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("error_loading_marks_entries")}
            </Alert>
          ) : !classSubjects || classSubjects.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("no_subjects_found_for_class")}
            </Alert>
          ) : subjectsEnrolledByStudents.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("no_subjects_enrolled_by_students")}
            </Alert>
          ) : filteredStudentsForStatus.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("no_students_found_for_class_session")}
            </Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        backgroundColor: '#f5f5f5',
                        fontWeight: 'bold',
                        minWidth: 200,
                        borderRight: '1px solid #e0e0e0'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" />
                        {t("student_name")}
                      </Box>
                    </TableCell>
                    {subjectsEnrolledByStudents.map((subject) => (
                      <TableCell
                        key={subject.id}
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor: '#f5f5f5',
                          minWidth: 120,
                          borderRight: '1px solid #e0e0e0'
                        }}
                      >
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography variant="caption" fontWeight="bold">
                            {subject.subjectCode}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {subject.name}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        minWidth: 150,
                        borderRight: '1px solid #e0e0e0'
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <AssessmentIcon fontSize="small" />
                        {t("action")}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudentsForStatus.map((student: any) => {
                    const studentName = getStudentDisplayName(student);
                    const studentId = student.id;

                    return (
                      <TableRow key={studentId} hover>
                        <TableCell
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            backgroundColor: '#ffffff',
                            fontWeight: 'medium',
                            borderRight: '1px solid #e0e0e0'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">{studentName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({student.rollNumber || 'N/A'})
                            </Typography>
                          </Box>
                        </TableCell>
                        {subjectsEnrolledByStudents.map((subject) => {
                          const hasMarks = hasMarksForSubject(studentId, subject.subjectCode);

                          return (
                            <TableCell
                              key={subject.id}
                              align="center"
                              sx={{
                                borderRight: '1px solid #e0e0e0',
                                backgroundColor: hasMarks ? '#e8f5e9' : '#fff'
                              }}
                            >
                              {hasMarks ? (
                                <CheckCircleIcon
                                  sx={{
                                    color: '#4caf50',
                                    fontSize: 24
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell
                          align="center"
                          sx={{
                            borderRight: '1px solid #e0e0e0',
                            backgroundColor: '#fff'
                          }}
                        >
                          {hasAllMarksForStudent(studentId) ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleCreateMarksheetFromEntry(studentId)}
                              disabled={createMarksheetMutation.isLoading}
                              sx={{
                                backgroundColor: '#3092e3',
                                '&:hover': { backgroundColor: '#2573c1' },
                                fontSize: '0.7rem',
                                px: 1,
                                py: 0.5,
                              }}
                            >
                              {createMarksheetMutation.isLoading ? (
                                <CircularProgress size={14} />
                              ) : (
                                t("create_marksheet")
                              )}
                            </Button>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {t("incomplete_marks")}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setViewMarksEntriesMarksheetType(DEFAULT_MARKSHEET_TYPE);
              setViewMarksEntriesMonthType("NA");
            }}
            color="secondary"
          >
            {t("reset_filters")}
          </Button>
          <Button
            onClick={() => {
              setViewMarksEntriesModalOpen(false);
              setViewMarksEntriesMarksheetType(DEFAULT_MARKSHEET_TYPE);
              setViewMarksEntriesMonthType("NA");
            }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkModalOpen}
        onClose={isGenerating ? undefined : handleCloseBulkModal}
        disableEscapeKeyDown={isGenerating}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {t("bulk_download_marksheets")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ py: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("class")}</InputLabel>
              <Select
                value={selectedClass?.id || ""}
                label={t("class")}
                disabled
              >
                <MenuItem value={selectedClass?.id || ""}>
                  {selectedClass
                    ? `${selectedClass.name} ${selectedClass.section}`
                    : t("no_class_selected")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("session")}</InputLabel>
              <Select
                value={selectedSession?.id || ""}
                label={t("session")}
                disabled
              >
                <MenuItem value={selectedSession?.id || ""}>
                  {selectedSession?.name || t("no_session_selected_first")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("marksheet_type")}</InputLabel>
              <Select
                value={selectedMarksheetType}
                label={t("marksheet_type")}
                onChange={(e) => setSelectedMarksheetType(e.target.value)}
                disabled={isGenerating}
              >
                {marksheetTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedMarksheetType === "MONTHLY" && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t("month_type")}</InputLabel>
                <Select
                  value={bulkMonthType}
                  label={t("month_type")}
                  onChange={(e) => setBulkMonthType(e.target.value)}
                  disabled={isGenerating}
                >
                  {monthTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedMarksheetType && selectedClass && selectedSession && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  {(() => {
                    const className = selectedClass?.name || "Unknown Class";
                    const sessionName =
                      selectedSession?.name || "Unknown Session";
                    const marksheetTypeLabel = marksheetTypeOptions.find(
                      (opt) => opt.value === selectedMarksheetType
                    )?.label || selectedMarksheetType;
                    return t("found_marksheets_for_type")
                      .replace("{count}", bulkModalFilteredMarksheets.length.toString())
                      .replace("{type}", marksheetTypeLabel)
                      .replace("{className}", className)
                      .replace("{sessionName}", sessionName);
                  })()}
                </Typography>
              </Box>
            )}

            {!isAllClassForApi && bulkModalFilteredMarksheets.length > 0 && (
              <TableContainer sx={{ maxHeight: 280, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper" }}>
                        <Checkbox
                          checked={bulkMarksheetsSelectAllChecked}
                          indeterminate={bulkMarksheetsSelectAllIndeterminate}
                          disabled={isGenerating}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkDownloadSelectedMarksheetIds(
                                new Set(
                                  bulkModalFilteredMarksheets
                                    .map((m: any) => m.id)
                                    .filter((id: unknown): id is number => typeof id === "number")
                                )
                              );
                            } else {
                              setBulkDownloadSelectedMarksheetIds(new Set());
                            }
                          }}
                          inputProps={{
                            "aria-label": (t("select_all") as string) || "Select All",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper" }}>
                        {(t("select_all") as string) || "Select All"} / {t("student_name")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkModalFilteredMarksheets.map((marksheet: any) => {
                      const s = marksheet.student || {};
                      const rowName =
                        s.studentName ||
                        (s.firstName && s.lastName
                          ? `${s.firstName} ${s.lastName}`
                          : s.firstName || s.lastName || "") ||
                        "Unknown Student";
                      const rowId = marksheet.id;
                      return (
                        <TableRow key={rowId} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={
                                typeof rowId === "number" &&
                                bulkDownloadSelectedMarksheetIds.has(rowId)
                              }
                              disabled={isGenerating || typeof rowId !== "number"}
                              onChange={(e) => {
                                if (typeof rowId !== "number") return;
                                const checked = e.target.checked;
                                setBulkDownloadSelectedMarksheetIds((prev) => {
                                  const next = new Set(prev);
                                  if (checked) next.add(rowId);
                                  else next.delete(rowId);
                                  return next;
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell>{rowName}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {isGenerating && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2">
                    {t("generating_pdf")} {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
                {currentStudent && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("processing").replace("{studentName}", currentStudent)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={isGenerating ? handleCancelBulkDownload : handleCloseBulkModal}
            variant="outlined"
          >
            {t("cancel")}
          </Button>

          <LoadingButton
            onClick={handleBulkDownload}
            variant="contained"
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
              minWidth: "200px",
              position: "relative",
              "&.Mui-disabled": {
                opacity: isGenerating ? 0.8 : 0.6,
              },
            }}
            loading={isGenerating}
            loadingPosition="start"
            disabled={(() => {
              if (!selectedMarksheetType || !selectedClass || !selectedSession) return true;
              if (effectiveSelectedMarksheetType === "MONTHLY" && !bulkMonthType) return true;

              if (isAllClassForApi) return true;

              return (
                bulkModalFilteredMarksheets.length === 0 ||
                bulkDownloadSelectedMarksheetIds.size === 0
              );
            })()}
          >
            {isGenerating
              ? t("generating_pdf")
              : t("download_marksheets").replace(
                  "{count}",
                  bulkDownloadSelectedMarksheetIds.size.toString()
                )
            }
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importModalOpen}
        onClose={handleCloseImportModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FileUploadIcon color="info" />
            <Typography variant="h6" fontWeight="bold">
              {t("excel_management")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ py: 2 }}>
            {importErrorFile && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t("some_entries_contain_errors_download_file")}
                </Alert>
                <Button
                  onClick={handleDownloadErrorFile}
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  fullWidth
                >
                  {t("download_error_file")}
                </Button>
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("class")}</InputLabel>
              <Select
                value={selectedClass?.id || ""}
                label={t("class")}
                disabled
              >
                <MenuItem value={selectedClass?.id || ""}>
                  {selectedClass
                    ? `${selectedClass.name} ${selectedClass.section}`
                    : t("no_class_selected")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("session")}</InputLabel>
              <Select
                value={selectedSession?.id || ""}
                label={t("session")}
                disabled
              >
                <MenuItem value={selectedSession?.id || ""}>
                  {selectedSession?.name || t("no_session_selected_first")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("marksheet_type")}</InputLabel>
              <Select
                value={importMarksheetType}
                label={t("marksheet_type")}
                onChange={(e) => {
                  const newMarksheetType = e.target.value;
                  setImportMarksheetType(newMarksheetType);
                  setImportErrorFile(null);
                  // Auto-set month type to "NA" for non-monthly marksheet types
                  if (shouldUseNAForMonthType(newMarksheetType)) {
                    setImportMonthType("NA");
                  } else if (newMarksheetType === "MONTHLY") {
                    // Reset to default month type when switching to MONTHLY
                    setImportMonthType(DEFAULT_MONTH_TYPE);
                  }
                }}
                disabled={importExcelMutation.isLoading}
              >
                {filteredMarksheetTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Month Type field - only show for MONTHLY marksheet type */}
            {importMarksheetType === "MONTHLY" && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t("month_type")}</InputLabel>
                <Select
                  value={importMonthType}
                  label={t("month_type")}
                  onChange={(e) => {
                    setImportMonthType(e.target.value);
                    setImportErrorFile(null);
                  }}
                  disabled={importExcelMutation.isLoading}
                >
                  {monthTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              onClick={handleExcelExport}
              variant="outlined"
              sx={{
                color: "#3092e3",
                borderColor: "#3092e3",
                width: "100%",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#3092e3",
                  color: "white",
                  borderColor: "#3092e3",
                },
              }}
              disabled={
                exportExcelMutation.isLoading ||
                importExcelMutation.isLoading ||
                !selectedClass ||
                !selectedSession ||
                !importMarksheetType
              }
              startIcon={
                exportExcelMutation.isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <FileDownloadIcon />
                )
              }
            >
              {exportExcelMutation.isLoading ? t("exporting") : t("get_excel")}
            </Button>
            <Box sx={{ mb: 2 }}>
              <input
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                id="excel-file-input"
                type="file"
                onChange={handleFileChange}
                onClick={(e) => {
                  setImportErrorFile(null);
                }}
                disabled={importExcelMutation.isLoading}
              />
              <label htmlFor="excel-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<FileUploadIcon />}
                  disabled={importExcelMutation.isLoading}
                  fullWidth
                  onClick={() => {
                    setImportErrorFile(null);
                  }}
                >
                  {selectedFile ? selectedFile.name : t("select_excel_file")}
                </Button>
              </label>
            </Box>

            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                <Typography variant="body2" color="info.contrastText">
                  {t("selected_file")
                    .replace("{fileName}", selectedFile.name)
                    .replace(
                      "{fileSize}",
                      (selectedFile.size / 1024).toFixed(2)
                    )}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseImportModal}
            variant="outlined"
            disabled={
              importExcelMutation.isLoading || exportExcelMutation.isLoading
            }
          >
            {t("cancel")}
          </Button>

          <LoadingButton
            onClick={handleExcelImport}
            variant="contained"
            color="info"
            loading={importExcelMutation.isLoading}
            loadingPosition="center"
            loadingIndicator={<CircularProgress size={20} color="inherit" />}
            disabled={!selectedFile || !selectedClass || !selectedSession}
            sx={{
              minWidth: "150px",
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
              "& .MuiLoadingButton-loadingIndicator": {
                position: "absolute",
              },
            }}
          >
            {t("import_excel")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={downloadMonthTypeModalOpen}
        onClose={handleCloseDownloadMonthTypeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FileDownloadIcon color="success" />
            <Typography variant="h6" fontWeight="bold">
              {t("select_month_type_for_download")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ py: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("class")}</InputLabel>
              <Select
                value={selectedClass?.id || ""}
                label={t("class")}
                disabled
              >
                <MenuItem value={selectedClass?.id || ""}>
                  {selectedClass
                    ? `${selectedClass.name} ${selectedClass.section}`
                    : t("no_class_selected")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("session")}</InputLabel>
              <Select
                value={selectedSession?.id || ""}
                label={t("session")}
                disabled
              >
                <MenuItem value={selectedSession?.id || ""}>
                  {selectedSession?.name || t("no_session_selected_first")}
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("marksheet_type")}</InputLabel>
              <Select
                value={importMarksheetType}
                label={t("marksheet_type")}
                onChange={(e) => {
                  const newMarksheetType = e.target.value;
                  setImportMarksheetType(newMarksheetType);
                  // Auto-set month type to "NA" for non-monthly marksheet types
                  if (shouldUseNAForMonthType(newMarksheetType)) {
                    setDownloadMonthType("NA");
                  } else if (newMarksheetType === "MONTHLY") {
                    // Reset to default month type when switching to MONTHLY
                    setDownloadMonthType(DEFAULT_MONTH_TYPE);
                  }
                }}
              >
                {downloadMarksheetTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Month Type field - only show for MONTHLY marksheet type */}
            {importMarksheetType === "MONTHLY" && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t("month_type")}</InputLabel>
                <Select
                  value={downloadMonthType}
                  label={t("month_type")}
                  onChange={(e) => setDownloadMonthType(e.target.value)}
                  disabled={downloadMarksheetMutation.isLoading}
                >
                  {monthTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
              <Typography variant="body2" color="info.contrastText">
                {t("download_marksheet_info")
                  .replace(
                    "{marksheetType}",
                    downloadMarksheetTypeOptions.find(
                      (opt) => opt.value === importMarksheetType
                    )?.label || importMarksheetType
                  )
                  .replace(
                    "{monthType}",
                    monthTypeOptions.find(
                      (opt) => opt.value === downloadMonthType
                    )?.label || downloadMonthType
                  )}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDownloadMonthTypeModal}
            variant="outlined"
            disabled={downloadMarksheetMutation.isLoading}
          >
            {t("cancel")}
          </Button>

          <LoadingButton
            onClick={handleDownloadMarksheetWithMonthType}
            variant="contained"
            color="success"
            loading={downloadMarksheetMutation.isLoading}
            loadingPosition="start"
            loadingIndicator={
              <Typography variant="body2" fontWeight="medium">
                {t("downloading")}
              </Typography>
            }
            disabled={!downloadMonthType}
            sx={{
              minWidth: "150px",
            }}
          >
            {t("download_excel")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle>{t("confirm_delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("are_you_sure_you_want_to_delete_this_student_this_action_cannot_be_undone")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteMarksheet}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              "&:hover": { backgroundColor: "#b71c1c" }
            }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleMenuClose(); handleViewMarksheet(menuRow); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" sx={{ color: "#3092e3" }} />
          </ListItemIcon>
          <ListItemText>{t("view")}</ListItemText>
        </MenuItem>

        {!isStudent && menuRow?.marksheetType !== "FINAL" && (
          <MenuItem onClick={() => handleEditMarksheet(menuRow)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("edit")}</ListItemText>
          </MenuItem>
        )}

        {!isStudent && (
          <MenuItem onClick={() => { handleMenuClose(); handlePublishMarksheet(menuRow); }}>
            <ListItemIcon>
              <PublishIcon
                fontSize="small"
                sx={{ color: menuRow?.published ? "#f44336" : "#4caf50" }}
              />
            </ListItemIcon>
            <ListItemText>{menuRow?.published ? t("unpublish") : t("publish")}</ListItemText>
          </MenuItem>
        )}

        {!isStudent && (
          <Tooltip
            title={
              String(
                menuRow?.promote ??
                  menuRow?.promotionStatus ??
                  menuRow?.promotion ??
                  menuRow?.student?.promote ??
                  menuRow?.student?.promotionStatus ??
                  menuRow?.student?.promotion ??
                  ""
              ).toUpperCase() === "PROMOTED"
                ? (t("cannot_delete_promoted_student_marksheet") ||
                    "Student is already promoted cannot delete its marksheet")
                : ""
            }
            arrow
            placement="left"
          >
            <span>
              <MenuItem
                disabled={
                  String(
                    menuRow?.promote ??
                      menuRow?.promotionStatus ??
                      menuRow?.promotion ??
                      menuRow?.student?.promote ??
                      menuRow?.student?.promotionStatus ??
                      menuRow?.student?.promotion ??
                      ""
                  ).toUpperCase() === "PROMOTED"
                }
                onClick={() => {
                  handleMenuClose();
                  handleDeleteMarksheet(menuRow);
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText sx={{ color: "error.main" }}>{t("delete")}</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        )}
      </Menu>

      {/* Bulk Publish/Unpublish Dialog */}
      <Dialog
        open={bulkPublishDialogOpen}
        onClose={closeBulkPublishDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold">
            {t("bulk_publish_unpublish") || "Bulk Publish/Unpublish"}
          </Typography>
          <IconButton onClick={closeBulkPublishDialog} disabled={publishBulkMarksheetsMutation.isLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>{t("action") || "Action"}</InputLabel>
              <Select
                value={bulkPublishAction}
                label={t("action") || "Action"}
                onChange={(e) => setBulkPublishAction(e.target.value as any)}
                disabled={publishBulkMarksheetsMutation.isLoading}
              >
                <MenuItem value="PUBLISH">{t("publish") || "Publish"}</MenuItem>
                <MenuItem value="UNPUBLISH">{t("unpublish") || "Unpublish"}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t("marksheet_type")}</InputLabel>
              <Select
                value={bulkPublishMarksheetType}
                label={t("marksheet_type")}
                onChange={(e) => {
                  const value = e.target.value;
                  setBulkPublishMarksheetType(value);
                  // Reset month filter when switching away from MONTHLY
                  if (value !== "MONTHLY") {
                    setBulkPublishMonthType("");
                  }
                }}
                disabled={publishBulkMarksheetsMutation.isLoading}
              >
                <MenuItem value="">
                  <em>{t("all") || "All"}</em>
                </MenuItem>
                {filteredMarksheetTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {bulkPublishMarksheetType === "MONTHLY" && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>{t("month_type")}</InputLabel>
                <Select
                  value={bulkPublishMonthType}
                  label={t("month_type")}
                  onChange={(e) => setBulkPublishMonthType(e.target.value)}
                  disabled={publishBulkMarksheetsMutation.isLoading}
                >
                  <MenuItem value="">
                    <em>{t("all") || "All"}</em>
                  </MenuItem>
                  {monthTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ flex: 1 }} />

            <Typography variant="body2" color="text.secondary">
              {(t("selected") || "Selected") + `: ${bulkSelectedCount}`}
            </Typography>
          </Box>

          {bulkPublishCandidates.length === 0 ? (
            <Alert severity="info">
              {t("no_marksheets_found") || "No marksheets found for the selected filters."}
            </Alert>
          ) : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={bulkAllSelected}
                        onChange={(e) => setBulkSelectAll(e.target.checked)}
                        disabled={publishBulkMarksheetsMutation.isLoading}
                      />
                    </TableCell>
                    <TableCell>{t("student_name")}</TableCell>
                    <TableCell>{t("roll_number")}</TableCell>
                    <TableCell>{t("class")}</TableCell>
                    <TableCell>{t("marksheet_type")}</TableCell>
                    <TableCell>{t("month_type")}</TableCell>
                    <TableCell>{t("session_year")}</TableCell>
                    <TableCell>{t("status") || "Status"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkPublishCandidates.map((row: any) => {
                    const checked = bulkSelectedMarksheetIds.has(row.id);
                    const published = Boolean(row.published);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleBulkSelected(row.id)}
                            disabled={publishBulkMarksheetsMutation.isLoading}
                          />
                        </TableCell>
                        <TableCell>{row.studentName || t("unknown_student")}</TableCell>
                        <TableCell>{row.rollNumber || t("not_available")}</TableCell>
                        <TableCell>{row.className || t("not_available")}</TableCell>
                        <TableCell>{row.marksheetType}</TableCell>
                        <TableCell>{row.monthType}</TableCell>
                        <TableCell>{row.sessionYear}</TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ color: published ? "success.main" : "text.secondary" }}
                          >
                            {published ? t("published") || "Published" : t("not_published") || "Not published"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeBulkPublishDialog} variant="outlined" disabled={publishBulkMarksheetsMutation.isLoading}>
            {t("cancel")}
          </Button>
          <LoadingButton
            onClick={handleBulkPublishSubmit}
            variant="contained"
            loading={publishBulkMarksheetsMutation.isLoading}
            disabled={bulkSelectedCount === 0 || bulkPublishCandidates.length === 0}
            startIcon={<PublishIcon />}
            sx={{
              backgroundColor: bulkPublishAction === "PUBLISH" ? "#4caf50" : "#f44336",
              "&:hover": {
                backgroundColor: bulkPublishAction === "PUBLISH" ? "#43a047" : "#e53935",
              },
              minWidth: 220,
            }}
          >
            {bulkPublishAction === "PUBLISH"
              ? (t("publish_selected") as any) || "Publish Selected"
              : (t("unpublish_selected") as any) || "Unpublish Selected"}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarkSheetMarksManagement;
export {};